import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faSpinner, 
  faTrash, 
  faPlus,
  faEdit,
  faCalendarAlt,
  faShoppingCart,
  faUser,
  faTruck,
  faCreditCard,
  faFileAlt,
  faMapMarkerAlt,
  faDollarSign,
  faInfoCircle,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';
import ComprovanteUpload from '../../components/comprovantes/ComprovanteUpload';
import { useNavigate, useParams } from 'react-router-dom';
import { pedidoService } from '../../services/pedidoService';
import { extensaoService } from '../../services/extensaoService';
import { pessoaService } from '../../services/pessoaService';
import formaEntregaService from '../../services/formaEntregaService';
import prazoEntregaService from '../../services/prazoEntregaService';
import formaPagamentoService from '../../services/formaPagamentoService';
import { tipoProdutoService } from '../../services/tipoProdutoService';
import cursoService from '../../services/cursoService';
import CursoSelector from '../../components/CursoSelector';
import { useAuth } from '../../contexts/AuthContext';
import produtoService from '../../services/produtoService';

// Estilos personalizados para scrollbar, animações e responsividade
const customStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  .items-container {
    position: relative;
    transition: max-height 0.3s ease-in-out;
  }
  
  .items-container.editing-mode {
    max-height: none !important;
  }
  
  .scroll-indicator {
    position: absolute;
    right: 8px;
    top: 8px;
    background: rgba(59, 130, 246, 0.1);
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    color: #3b82f6;
    border: 1px solid rgba(59, 130, 246, 0.2);
    z-index: 30;
    pointer-events: none;
  }
  
  .floating-add-btn {
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  }
  
  .floating-add-btn:hover {
    box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
  }
  
  .animate-fade-in {
    animation: fadeIn 0.2s ease-in-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Estilos específicos para mobile */
  @media (max-width: 640px) {
    .mobile-optimized {
      touch-action: manipulation;
    }
    
    .mobile-tap-target {
      min-height: 44px;
      min-width: 44px;
    }
    
    .mobile-form-spacing {
      padding: 12px;
    }
    
    /* Melhor visualização de inputs no mobile */
    input[type="number"], 
    input[type="date"], 
    select {
      font-size: 16px !important; /* Previne zoom automático no iOS */
    }
    
    /* Otimização do scroll no mobile */
    .items-container {
      -webkit-overflow-scrolling: touch;
    }
    
    /* Botões mais acessíveis no mobile */
    button {
      touch-action: manipulation;
    }
  }
`;

// Adicionar estilos ao head se ainda não existirem
if (!document.getElementById('pedido-custom-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'pedido-custom-styles';
  styleElement.textContent = customStyles;
  document.head.appendChild(styleElement);
}

// Componente para exibir erros de validação inline
const ValidationError = ({ error }) => {
  if (!error) return null;
  return (
    <p className="text-xs text-red-600 mt-1 flex items-center">
      <FontAwesomeIcon icon={faTimes} className="mr-1" />
      {error}
    </p>
  );
};

// Componente de Busca/Select Customizado
const SearchableSelect = ({ 
  label, 
  placeholder, 
  searchValue, 
  onSearchChange, 
  options, 
  onSelect, 
  showDropdown, 
  onToggleDropdown,
  loading = false,
  error = null,
  required = false,
  disabled = false,
  noOptionsMessage = "Nenhuma opção encontrada",
  onKeyDown = null, // Para navegação por teclado
  onClearSearch = null // Callback para limpar busca ao perder foco
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const handleKeyDown = (e) => {
    if (!showDropdown || options.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          onSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onToggleDropdown(false);
        break;
      case 'Tab':
        if (highlightedIndex >= 0) {
          e.preventDefault();
          onSelect(options[highlightedIndex]);
        }
        break;
    }
    
    // Chamar callback personalizado se fornecido
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Reset highlighted index quando dropdown abrir/fechar ou opções mudarem
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [showDropdown, options]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => onToggleDropdown(true)}
          onBlur={() => {
            // Pequeno delay para permitir clique em item do dropdown
            setTimeout(() => {
              onToggleDropdown(false);
              // NÃO limpar o campo ao perder foco - manter valor selecionado visível
            }, 200);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
            error 
              ? 'border-red-300 bg-red-50' 
              : 'border-gray-300 hover:border-gray-400'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          placeholder={loading ? 'Carregando...' : placeholder}
        />
        <div 
          className="absolute right-2 top-2 cursor-pointer text-gray-400"
          onClick={() => !disabled && onToggleDropdown(!showDropdown)}
        >
          <FontAwesomeIcon icon={showDropdown ? faTimes : faPlus} />
        </div>
        
        {showDropdown && options.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {options.map((option, index) => (
              <div
                key={option.id}
                onClick={() => onSelect(option)}
                className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  index === highlightedIndex 
                    ? 'bg-green-100 text-green-800' 
                    : 'hover:bg-green-50'
                }`}
              >
                {option.nome}
              </div>
            ))}
          </div>
        )}
        
        {showDropdown && options.length === 0 && !loading && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="px-3 py-2 text-gray-500 text-center">
              {noOptionsMessage}
            </div>
          </div>
        )}
      </div>
      <ValidationError error={error} />
    </div>
  );
};

// Componente para Item do Pedido
const ItemPedidoCard = ({ item, index, itens, onUpdate, onRemove, produtos, tipos, pessoasExtensao, onEditModeChange, onSaveSuccess, formaPagamentoId, formasPagamento, extensaoId }) => {
  const { user } = useAuth(); // Acesso ao usuário para verificar nível
  
  // Verificar se é usuário com nível abaixo de 4 (restrições aplicadas)
  const isRestrictedUser = () => {
    return user && user.nivel_acesso < 4;
  };
  
  // Verificar se a forma de pagamento é "Troca por Livros" (permite valor 0)
  const isTrocaPorLivros = () => {
    if (!formaPagamentoId || !formasPagamento) return false;
    const formaSelecionada = formasPagamento.find(f => f.idFormaPagamento?.toString() === formaPagamentoId?.toString());
    return formaSelecionada?.nome?.toLowerCase().includes('troca') && formaSelecionada?.nome?.toLowerCase().includes('livro');
  };
  
  // Função para formatação de valores monetários no padrão brasileiro
  const formatCurrency = (value) => {
    const numValue = Number(value) || 0;
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const [editMode, setEditMode] = useState(item.isNew || false);
  const [formItem, setFormItem] = useState(item);
  const [produtosPorTipo, setProdutosPorTipo] = useState([]);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  
  // Estados para pessoa destinatária
  const [pessoasDisponiveis, setPessoasDisponiveis] = useState([]);
  const [loadingPessoasItem, setLoadingPessoasItem] = useState(false);
  const [tipoPermitePessoa, setTipoPermitePessoa] = useState(false);
  
  // Estados para campos de matrícula
  const [isMatricula, setIsMatricula] = useState(false);
  const [cursosDisponiveis, setCursosDisponiveis] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);
  
  // Estados para controle de foco e workflow
  const tipoInputRef = useRef(null);
  const produtoInputRef = useRef(null);
  const valorInputRef = useRef(null);
  const quantidadeInputRef = useRef(null);
  const observacaoInputRef = useRef(null);
  const pessoaInputRef = useRef(null);

  // Auto-foco inteligente quando entrar em modo de edição
  useEffect(() => {
    if (editMode) {
      // Focar no primeiro campo vazio ou no tipo se for um item novo
      setTimeout(() => {
        if (!formItem.tipoProduto) {
          tipoInputRef.current?.focus();
        } else if (!formItem.produto) {
          produtoInputRef.current?.focus();
        } else if (!formItem.valorUnitario) {
          valorInputRef.current?.focus();
        } else if (!formItem.quantidade || formItem.quantidade === 0) {
          quantidadeInputRef.current?.focus();
        }
      }, 100);
    }
  }, [editMode, formItem.tipoProduto, formItem.produto]);

  // Atalhos de teclado globais para o item
  const handleItemKeyDown = useCallback((e) => {
    // Ctrl+S para salvar
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
    // Escape para cancelar
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
    // Ctrl+Enter para salvar e adicionar novo item
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
      // Simular clique no botão de adicionar item (se existir)
      setTimeout(() => {
        const addButton = document.querySelector('[data-add-item]');
        if (addButton) addButton.click();
      }, 100);
    }
  }, []);

  // Workflow inteligente - navegar entre campos com Tab/Enter
  const handleFieldKeyDown = (e, nextFieldRef = null) => {
    if (e.key === 'Enter' && nextFieldRef?.current) {
      e.preventDefault();
      nextFieldRef.current.focus();
    }
  };
  const [produtoNome, setProdutoNome] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [searchTipo, setSearchTipo] = useState(''); // Busca para tipos
  const [searchProduto, setSearchProduto] = useState(''); // Busca para produtos
  const [showTipoDropdown, setShowTipoDropdown] = useState(false);
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  const [showObservacao, setShowObservacao] = useState(false); // Estado para controlar exibição da observação
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Estado para confirmação de exclusão
  const [itemToDelete, setItemToDelete] = useState(null); // Item a ser excluído
  
  // Ref para controlar inicialização do searchProduto (evitar repopular durante digitação)
  const searchProdutoInitialized = useRef(false);

  // Se é um item novo, entrar automaticamente em modo de edição
  useEffect(() => {
    if (item.isNew) {
      setEditMode(true);
    }
  }, [item.isNew]);

  // Notificar componente pai quando modo de edição muda
  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(index, editMode);
    }
  }, [editMode, index, onEditModeChange]);

  // Atualizar formItem quando item props mudar (para casos de reload)
  useEffect(() => {
    setFormItem(item);
  }, [item]);

  // Carregar nome do produto quando não estiver disponível no array de produtos
  useEffect(() => {
    const carregarNomeProduto = async () => {
      if (!editMode && (item.produto || item.idProduto)) {
        // Primeiro tenta encontrar no array de produtos passado como prop
        const produtoEncontrado = produtos.find(p => p.idProduto == (item.produto || item.idProduto));
        
        if (produtoEncontrado) {
          setProdutoNome(produtoEncontrado.nome);
        } else if (item.tipoProduto || item.idTipoProduto) {
          // Se não encontrou, carrega produtos do tipo para buscar o nome
          try {
            const tipoId = item.tipoProduto || item.idTipoProduto;
            const response = await produtoService.getByTipo(tipoId, extensaoId);
            if (response.success && Array.isArray(response.data)) {
              const produto = response.data.find(p => p.idProduto == (item.produto || item.idProduto));
              setProdutoNome(produto?.nome || null);
            }
          } catch (error) {
            console.error('Erro ao carregar nome do produto:', error);
            setProdutoNome(null);
          }
        }
      }
    };

    carregarNomeProduto();
  }, [item.produto, item.idProduto, item.tipoProduto, item.idTipoProduto, editMode, produtos]);

  // Verificar se o tipo de produto permite pessoa destinatária e carregar pessoas disponíveis
  useEffect(() => {
    const verificarTipoECarregarPessoas = async () => {
      if (formItem.tipoProduto) {
        try {
          // Verificar se o tipo permite pessoa
          const tipoEncontrado = tipos.find(t => t.idTipoProduto == formItem.tipoProduto);
          const permitePessoa = tipoEncontrado?.permiteVinculoPessoa === 1;
          setTipoPermitePessoa(permitePessoa);
          
          // Se permite pessoa, carregar lista de pessoas da extensão
          if (permitePessoa && pessoasExtensao && pessoasExtensao.length > 0) {
            setPessoasDisponiveis(pessoasExtensao);
          } else if (permitePessoa) {
            // Se não tem pessoas carregadas ainda, tentar carregar
            setLoadingPessoasItem(true);
            const response = await pessoaService.getAll();
            if (response.success) {
              setPessoasDisponiveis(response.data || []);
            }
            setLoadingPessoasItem(false);
          } else {
            setPessoasDisponiveis([]);
          }
        } catch (error) {
          console.error('Erro ao verificar tipo e carregar pessoas:', error);
          setTipoPermitePessoa(false);
          setPessoasDisponiveis([]);
          setLoadingPessoasItem(false);
        }
      } else {
        setTipoPermitePessoa(false);
        setPessoasDisponiveis([]);
      }
    };

    verificarTipoECarregarPessoas();
  }, [formItem.tipoProduto, tipos, pessoasExtensao]);

  // Verificar se é matrícula e carregar cursos disponíveis
  useEffect(() => {
    const verificarMatriculaECarregarCursos = async () => {
      console.log('=== DEBUG MATRÍCULA ===');
      console.log('formItem.tipoProduto:', formItem.tipoProduto);
      console.log('formItem.produto:', formItem.produto);
      
      if (formItem.tipoProduto && formItem.produto) {
        try {
          // Verificar se o tipo é "requerimento" e o produto é "matrícula"
          const tipoEncontrado = tipos.find(t => t.idTipoProduto == formItem.tipoProduto);
          const produtoEncontrado = produtos.find(p => p.idProduto == formItem.produto);
          
          console.log('tipoEncontrado:', tipoEncontrado);
          console.log('produtoEncontrado:', produtoEncontrado);
          
          // Se não encontrou o produto no array atual, buscar no array produtosPorTipo
          const produtoFinal = produtoEncontrado || produtosPorTipo.find(p => p.idProduto == formItem.produto);
          
          console.log('produtoFinal:', produtoFinal);
          
          const isRequerimento = tipoEncontrado?.nome?.toLowerCase().includes('requerimento');
          const isMatriculaProduto = produtoFinal?.nome?.toLowerCase().includes('matrícula') || 
                                   produtoFinal?.nome?.toLowerCase().includes('matricula');
          
          console.log('isRequerimento:', isRequerimento);
          console.log('isMatriculaProduto:', isMatriculaProduto);
          
          const isMatriculaItem = isRequerimento && isMatriculaProduto;
          console.log('isMatriculaItem FINAL:', isMatriculaItem);
          
          setIsMatricula(isMatriculaItem);
          
          // Se for matrícula, carregar cursos disponíveis
          if (isMatriculaItem) {
            console.log('🎓 Detectada matrícula! Carregando cursos...');
            setLoadingCursos(true);
            try {
              const response = await cursoService.list({ limit: 1000 });
              console.log('Resposta cursos:', response);
              if (response.success && response.data) {
                setCursosDisponiveis(response.data);
                console.log('Cursos carregados:', response.data.length);
              } else {
                setCursosDisponiveis([]);
              }
            } catch (error) {
              console.error('Erro ao carregar cursos:', error);
              setCursosDisponiveis([]);
            } finally {
              setLoadingCursos(false);
            }
          } else {
            console.log('❌ Não é matrícula, limpando cursos');
            setCursosDisponiveis([]);
          }
          
        } catch (error) {
          console.error('Erro ao verificar matrícula e carregar cursos:', error);
          setIsMatricula(false);
          setCursosDisponiveis([]);
          setLoadingCursos(false);
        }
      } else {
        console.log('❌ Faltam dados: tipoProduto ou produto');
        setIsMatricula(false);
        setCursosDisponiveis([]);
      }
      console.log('======================');
    };

    verificarMatriculaECarregarCursos();
  }, [formItem.tipoProduto, formItem.produto, tipos, produtos, produtosPorTipo]);

  // Função de validação inline
  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };
    
    switch (fieldName) {
      case 'tipoProduto':
        if (!value) {
          errors.tipoProduto = 'Selecione um tipo de produto';
        } else {
          delete errors.tipoProduto;
        }
        break;
      case 'produto':
        if (!value) {
          errors.produto = 'Selecione um produto';
        } else {
          delete errors.produto;
        }
        break;
      case 'quantidade':
        const quantidade = parseInt(value);
        if (!value || quantidade <= 0) {
          errors.quantidade = 'Informe uma quantidade válida (maior que 0)';
        } else {
          delete errors.quantidade;
        }
        break;
      case 'valorUnitario':
        const valor = parseFloat(value);
        // Permitir valor 0 se o item for marcado como "Troca por Livros" OU se a forma de pagamento do pedido for troca
        if (formItem.trocaPorLivros || isTrocaPorLivros()) {
          if (value === '' || value === null || value === undefined || isNaN(valor) || valor < 0) {
            errors.valorUnitario = 'Informe um valor unitário válido (0 ou maior)';
          } else {
            delete errors.valorUnitario;
          }
        } else {
          if (!value || valor <= 0) {
            errors.valorUnitario = 'Informe um valor unitário válido (maior que 0)';
          } else {
            delete errors.valorUnitario;
          }
        }
        break;
      case 'pessoaDestinataria':
        // Campo pessoa destinatária agora é opcional
        // if (tipoPermitePessoa && !value) {
        //   errors.pessoaDestinataria = 'Selecione a pessoa destinatária';
        // } else {
        //   delete errors.pessoaDestinataria;
        // }
        delete errors.pessoaDestinataria; // Sempre limpar erro pois é opcional
        break;
      case 'curso':
        if (isMatricula && !value) {
          errors.curso = 'Selecione um curso para a matrícula';
        } else {
          delete errors.curso;
        }
        break;
      case 'tipoCurso':
        if (isMatricula && !value) {
          errors.tipoCurso = 'Selecione o tipo do curso';
        } else {
          delete errors.tipoCurso;
        }
        break;
      case 'periodo':
        if (isMatricula && !value) {
          errors.periodo = 'Selecione o período do curso';
        } else {
          delete errors.periodo;
        }
        break;
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validação completa antes de salvar
  const validateForm = () => {
    const errors = {};
    
    if (!formItem.tipoProduto) errors.tipoProduto = 'Selecione um tipo de produto';
    if (!formItem.produto) errors.produto = 'Selecione um produto';
    if (!formItem.quantidade || parseInt(formItem.quantidade) <= 0) {
      errors.quantidade = 'Informe uma quantidade válida (maior que 0)';
    }
    // Permitir valor 0 se o item for marcado como "Troca por Livros" OU se a forma de pagamento do pedido for troca
    const valorUnit = parseFloat(formItem.valorUnitario);
    if (formItem.trocaPorLivros || isTrocaPorLivros()) {
      if (formItem.valorUnitario === '' || formItem.valorUnitario === null || formItem.valorUnitario === undefined || isNaN(valorUnit) || valorUnit < 0) {
        errors.valorUnitario = 'Informe um valor unitário válido (0 ou maior)';
      }
    } else {
      if (!formItem.valorUnitario || valorUnit <= 0) {
        errors.valorUnitario = 'Informe um valor unitário válido (maior que 0)';
      }
    }
    
    // Validar pessoa destinatária se o tipo permitir (OPCIONAL)
    // if (tipoPermitePessoa && !formItem.pessoaDestinataria) {
    //   errors.pessoaDestinataria = 'Selecione a pessoa destinatária';
    // }
    
    // Validar campos específicos para matrícula
    if (isMatricula) {
      if (!formItem.curso) errors.curso = 'Selecione um curso para a matrícula';
      if (!formItem.tipoCurso) errors.tipoCurso = 'Selecione o tipo do curso';
      if (!formItem.periodo) errors.periodo = 'Selecione o período do curso';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Carregar produtos quando o tipo mudar OU quando entrar em modo de edição
  useEffect(() => {
    const loadProdutos = async () => {
      if (formItem.tipoProduto) {
        setLoadingProdutos(true);
        try {
          const response = await produtoService.getByTipo(formItem.tipoProduto, extensaoId);
          console.log('Resposta da API produtos:', response);
          
          if (response.success && Array.isArray(response.data)) {
            console.log('Produtos carregados:', response.data);
            setProdutosPorTipo(response.data);
          } else {
            console.error('Erro ao carregar produtos:', response.error || 'Estrutura de resposta inválida');
            setProdutosPorTipo([]);
          }
        } catch (error) {
          console.error('Erro ao carregar produtos:', error);
          setProdutosPorTipo([]);
        } finally {
          setLoadingProdutos(false);
        }
      } else {
        setProdutosPorTipo([]);
      }
    };

    loadProdutos();
  }, [formItem.tipoProduto]);

  // Carregar produtos quando entrar em modo de edição (se já tem tipoProduto)
  useEffect(() => {
    if (editMode && (item.tipoProduto || item.idTipoProduto) && produtosPorTipo.length === 0) {
      const loadProdutosIniciais = async () => {
        setLoadingProdutos(true);
        try {
          const tipoId = item.tipoProduto || item.idTipoProduto;
          console.log('Carregando produtos para tipo:', tipoId);
          const response = await produtoService.getByTipo(tipoId, extensaoId);
          if (response.success && Array.isArray(response.data)) {
            console.log('Produtos carregados para edição:', response.data);
            setProdutosPorTipo(response.data);
          }
        } catch (error) {
          console.error('Erro ao carregar produtos iniciais:', error);
          setProdutosPorTipo([]);
        } finally {
          setLoadingProdutos(false);
        }
      };
      loadProdutosIniciais();
    }
  }, [editMode, item.tipoProduto, item.idTipoProduto, produtosPorTipo.length]);

  // Filtrar tipos baseado na busca
  const filteredTipos = tipos.filter(tipo => 
    tipo.nome.toLowerCase().includes(searchTipo.toLowerCase())
  );

  // Filtrar produtos baseado na busca
  const filteredProdutos = produtosPorTipo.filter(produto => 
    produto.nome.toLowerCase().includes(searchProduto.toLowerCase())
  );

  // Selecionar tipo
  const selectTipo = (tipo) => {
    setFormItem({
      ...formItem, 
      tipoProduto: tipo.idTipoProduto,
      produto: '' // Limpar produto quando tipo mudar
    });
    setSearchTipo(tipo.nome); // Mostrar tipo selecionado
    setShowTipoDropdown(false);
    setSearchProduto(''); // Limpar busca de produto
    validateField('tipoProduto', tipo.idTipoProduto);
  };

  // Selecionar produto
  const selectProduto = (produto) => {
    const updateData = {
      ...formItem, 
      produto: produto.idProduto
    };
    
    // Para usuários restritos, preencher valor automaticamente do produto
    // Usar valorAplicado (preço customizado se houver) ou valor padrão
    if (isRestrictedUser() && (produto.valorAplicado || produto.valor)) {
      updateData.valorUnitario = produto.valorAplicado || produto.valor;
    }
    
    setFormItem(updateData);
    setSearchProduto(produto.nome); // Mostrar produto selecionado
    setShowProdutoDropdown(false);
    validateField('produto', produto.idProduto);
    
    // Marcar que foi inicializado para evitar repovoamento
    searchProdutoInitialized.current = true;
    
    // Se preencheu o valor automaticamente, focar na quantidade
    if (isRestrictedUser() && (produto.valorAplicado || produto.valor)) {
      setTimeout(() => quantidadeInputRef.current?.focus(), 100);
    }
  };

  const handleSave = () => {
    // Validação usando o novo sistema
    if (!validateForm()) {
      return; // Para se houver erros de validação
    }
    
    // Buscar e incluir os nomes do tipo e produto
    const tipoEncontrado = tipos.find(t => t.idTipoProduto == formItem.tipoProduto);
    const produtoEncontrado = produtosPorTipo.find(p => p.idProduto == formItem.produto);
    
    // A validação de duplicidade será feita no updateItem
    
    // DEBUG específico para campos de matrícula antes de salvar
    if (isMatricula) {
      console.log('=== DEBUG SALVAMENTO MATRÍCULA ===');
      console.log('isMatricula:', isMatricula);
      console.log('formItem.curso:', formItem.curso, typeof formItem.curso);
      console.log('formItem.tipoCurso:', formItem.tipoCurso, typeof formItem.tipoCurso);
      console.log('formItem.periodo:', formItem.periodo, typeof formItem.periodo);
      console.log('cursosDisponiveis:', cursosDisponiveis);
      console.log('Curso encontrado:', cursosDisponiveis.find(c => c.idCurso == formItem.curso));
      console.log('=================================');
    } else {
      console.log('⚠️ isMatricula é FALSE! Item não será salvo com campos de matrícula');
      console.log('formItem.tipoProduto:', formItem.tipoProduto);
      console.log('formItem.produto:', formItem.produto);
    }
    
    const itemCompleto = {
      ...formItem,
      quantidade: parseInt(formItem.quantidade) || 1,
      valorUnitario: parseFloat(formItem.valorUnitario) || 0,
      tipoProdutoNome: tipoEncontrado?.nome || '',
      produtoNome: produtoEncontrado?.nome || '',
      // Campo de troca por livros
      trocaPorLivros: !!formItem.trocaPorLivros,
      // Campos específicos para matrícula - sempre incluir se estiverem preenchidos
      curso: formItem.curso ? parseInt(formItem.curso) : null,
      tipoCurso: formItem.tipoCurso || null,
      periodo: formItem.periodo || null,
      cursoNome: formItem.curso && cursosDisponiveis.length > 0 
        ? cursosDisponiveis.find(c => c.idCurso == formItem.curso)?.nome 
        : null
    };
    
    console.log('=== DEBUG ITEM COMPLETO ===');
    console.log('isMatricula:', isMatricula);
    console.log('formItem.curso:', formItem.curso);
    console.log('formItem.tipoCurso:', formItem.tipoCurso);
    console.log('formItem.periodo:', formItem.periodo);
    console.log('formItem.trocaPorLivros:', formItem.trocaPorLivros);
    console.log('itemCompleto:', itemCompleto);
    console.log('itemCompleto.curso:', itemCompleto.curso, typeof itemCompleto.curso);
    console.log('itemCompleto.tipoCurso:', itemCompleto.tipoCurso, typeof itemCompleto.tipoCurso);
    console.log('itemCompleto.periodo:', itemCompleto.periodo, typeof itemCompleto.periodo);
    console.log('itemCompleto.trocaPorLivros:', itemCompleto.trocaPorLivros);
    console.log('==========================');
    
    onUpdate(index, itemCompleto);
    setEditMode(false);
    setValidationErrors({}); // Limpar erros após salvar
    
    // Notificar o componente pai sobre o sucesso (para mensagem global)
    if (onSaveSuccess) {
      onSaveSuccess();
    }
    
    // Notificar mudança de modo de edição
    if (onEditModeChange) {
      onEditModeChange(index, false);
    }
  };

  const handleCancel = () => {
    // Se é um item novo e não tem dados preenchidos, remover o item
    if (item.isNew && (!formItem.produto || !formItem.tipoProduto)) {
      onRemove(index);
    } else {
      setFormItem(item);
      setEditMode(false);
      setProdutosPorTipo([]); // Limpar produtos ao cancelar
      
      // Notificar mudança de modo de edição
      if (onEditModeChange) {
        onEditModeChange(index, false);
      }
    }
  };

  // Função para iniciar processo de exclusão
  const handleDeleteClick = () => {
    setItemToDelete(index);
    setShowDeleteConfirm(true);
  };

  // Função para confirmar exclusão
  const handleDeleteConfirm = () => {
    if (itemToDelete !== null) {
      onRemove(itemToDelete);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  // Função para cancelar exclusão
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // useEffect para preencher campo de busca do produto APENAS quando entrar em modo de edição
  // Usado ref para rastrear se já inicializou para não interferir com digitação do usuário
  useEffect(() => {
    if (editMode && formItem.produto && produtosPorTipo.length > 0 && !searchProdutoInitialized.current) {
      const produtoEncontrado = produtosPorTipo.find(p => p.idProduto == formItem.produto);
      console.log('=== DEBUG: Inicializando searchProduto para edição ===');
      console.log('formItem.produto:', formItem.produto);
      console.log('produtoEncontrado:', produtoEncontrado);
      
      if (produtoEncontrado) {
        console.log('Definindo searchProduto inicial:', produtoEncontrado.nome);
        setSearchProduto(produtoEncontrado.nome);
        searchProdutoInitialized.current = true;
      }
    }
    
    // Reset do flag quando sair do modo de edição
    if (!editMode) {
      searchProdutoInitialized.current = false;
    }
  }, [editMode, formItem.produto, produtosPorTipo]);

  const handleEdit = () => {
    // Garantir que o formItem tenha todos os dados do item ao entrar em edição
    const mappedItem = {
      ...item,
      tipoProduto: item.tipoProduto || item.idTipoProduto,
      produto: item.produto || item.idProduto,
      valorUnitario: item.valorUnitario || 0,
      quantidade: item.quantidade || 1,
      observacao: item.observacao || item.detalhamento || '',
      trocaPorLivros: !!item.trocaPorLivros
    };
    
    // Preencher searchTipo com o nome do tipo existente
    const tipoEncontrado = tipos.find(t => t.idTipoProduto == mappedItem.tipoProduto);
    if (tipoEncontrado) {
      setSearchTipo(tipoEncontrado.nome);
    } else {
      setSearchTipo('');
    }
    
    console.log('Item original:', item);
    console.log('Item mapeado para edição:', mappedItem);
    console.log('Tipo encontrado:', tipoEncontrado);
    
    setFormItem(mappedItem);
    setEditMode(true);
    
    // Notificar mudança de modo de edição
    if (onEditModeChange) {
      onEditModeChange(index, true);
    }
    
    // Limpar erros de validação
    setValidationErrors({});
    
    // Limpar searchProduto - será preenchido pelo useEffect
    setSearchProduto('');
    
    // Reset do flag de inicialização do searchProduto
    searchProdutoInitialized.current = false;
  };


  return (
    <div 
      className={`bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md relative ${
        editMode ? 'p-3' : 'p-1'
      }`}
      onKeyDown={editMode ? handleItemKeyDown : undefined}
      tabIndex={editMode ? 0 : -1}
    >
      
      {editMode ? (
        <div className="space-y-3 sm:space-y-2">
          {/* Dica de atalhos - mais compacta no mobile */}
          <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
            <span className="hidden sm:block">💡 Dicas: Tab/Enter para navegar • Ctrl+S para salvar • Esc para fechar</span>
            <span className="sm:hidden">💡 Ctrl+S salvar • Esc fechar</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-2">
            <SearchableSelect
              label="Tipo"
              placeholder="Digite para buscar ou selecione um tipo..."
              searchValue={searchTipo}
              onSearchChange={(value) => {
                setSearchTipo(value);
                setShowTipoDropdown(true);
              }}
              options={filteredTipos}
              onSelect={(tipo) => {
                selectTipo(tipo);
                setShowTipoDropdown(false);
                // Auto-foco no próximo campo após seleção
                setTimeout(() => produtoInputRef.current?.focus(), 100);
              }}
              showDropdown={showTipoDropdown}
              onToggleDropdown={(show) => {
                setShowTipoDropdown(show);
              }}
              error={validationErrors.tipoProduto}
              required={true}
              onKeyDown={(e) => handleFieldKeyDown(e, produtoInputRef)}
            />

            <SearchableSelect
              label="Produto"
              placeholder={
                !formItem.tipoProduto 
                  ? "Selecione um tipo primeiro" 
                  : "Digite para buscar ou selecione um produto..."
              }
              searchValue={searchProduto}
              onSearchChange={(value) => {
                setSearchProduto(value);
                setShowProdutoDropdown(true);
              }}
              options={filteredProdutos}
              onSelect={(produto) => {
                selectProduto(produto);
                setShowProdutoDropdown(false);
                // Auto-foco no valor após seleção do produto
                setTimeout(() => valorInputRef.current?.focus(), 100);
              }}
              showDropdown={showProdutoDropdown}
              onToggleDropdown={(show) => {
                setShowProdutoDropdown(show);
              }}
              disabled={!formItem.tipoProduto}
              error={validationErrors.produto}
              required={true}
              loading={loadingProdutos}
              noOptionsMessage="Nenhum produto encontrado para este tipo"
              onKeyDown={(e) => handleFieldKeyDown(e, valorInputRef)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor Unitário *
                {isRestrictedUser() && (
                  <span className="text-xs text-gray-500 ml-1">(automático)</span>
                )}
              </label>
              <input
                ref={valorInputRef}
                type="number"
                step="0.01"
                value={formItem.valorUnitario || ''}
                onChange={(e) => {
                  const novoValor = e.target.value;
                  setFormItem({...formItem, valorUnitario: novoValor});
                  validateField('valorUnitario', novoValor);
                }}
                onKeyDown={(e) => handleFieldKeyDown(e, quantidadeInputRef)}
                readOnly={isRestrictedUser()} // Bloquear para usuários restritos
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors text-sm ${
                  isRestrictedUser() 
                    ? 'bg-gray-50 cursor-not-allowed text-gray-600' // Estilo para readonly
                    : validationErrors.valorUnitario 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="0,00"
                title={isRestrictedUser() ? 'Valor preenchido automaticamente do produto' : ''}
              />
              <ValidationError error={validationErrors.valorUnitario} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantidade *
                {formItem.pessoaDestinataria && (
                  <span className="text-xs text-blue-600 ml-2 font-normal">
                    (fixado em 1 para pessoa destinatária)
                  </span>
                )}
              </label>
              <input
                ref={quantidadeInputRef}
                type="number"
                min="1"
                value={formItem.quantidade || ''}
                onChange={(e) => {
                  // Se pessoa destinatária estiver selecionada, ignorar mudanças
                  if (formItem.pessoaDestinataria) {
                    return;
                  }
                  
                  const novaQuantidade = e.target.value;
                  setFormItem({...formItem, quantidade: parseInt(novaQuantidade) || 0});
                  validateField('quantidade', novaQuantidade);
                }}
                onKeyDown={(e) => handleFieldKeyDown(e, observacaoInputRef)}
                disabled={!!formItem.pessoaDestinataria}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors text-sm ${
                  validationErrors.quantidade 
                    ? 'border-red-300 bg-red-50' 
                    : formItem.pessoaDestinataria
                    ? 'border-blue-300 bg-blue-50 cursor-not-allowed'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="1"
                title={formItem.pessoaDestinataria ? "Quantidade fixada em 1 para pessoa destinatária" : ""}
              />
              <ValidationError error={validationErrors.quantidade} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtotal
              </label>
              <input
                type="text"
                value={formatCurrency((formItem.quantidade || 0) * (formItem.valorUnitario || 0))}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed font-medium text-sm"
              />
            </div>
          </div>

          {/* Campo de Pessoa Destinatária - mostrar apenas se o tipo permitir */}
          {tipoPermitePessoa && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pessoa Destinatária
              </label>
              <select
                ref={pessoaInputRef}
                value={formItem.pessoaDestinataria || ''}
                onChange={(e) => {
                  const pessoaId = e.target.value;
                  // Se uma pessoa foi selecionada, fixar quantidade em 1
                  const updatedItem = {
                    ...formItem, 
                    pessoaDestinataria: pessoaId
                  };
                  
                  // Se pessoa foi selecionada, fixar quantidade em 1
                  if (pessoaId) {
                    updatedItem.quantidade = 1;
                  }
                  
                  setFormItem(updatedItem);
                  validateField('pessoaDestinataria', pessoaId);
                  
                  // Validar quantidade se foi alterada
                  if (pessoaId) {
                    validateField('quantidade', 1);
                  }
                }}
                disabled={loadingPessoasItem || pessoasDisponiveis.length === 0}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                  validationErrors.pessoaDestinataria 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-300 hover:border-gray-400'
                } ${loadingPessoasItem ? 'bg-gray-50 cursor-wait' : ''}`}
              >
                <option value="">
                  {loadingPessoasItem ? 'Carregando...' : 'Selecione a pessoa destinatária'}
                </option>
                {pessoasDisponiveis.map(pessoa => (
                  <option key={pessoa.idPessoa} value={pessoa.idPessoa}>
                    {pessoa.nome}
                  </option>
                ))}
              </select>
              <ValidationError error={validationErrors.pessoaDestinataria} />
              {formItem.pessoaDestinataria && (
                <p className="text-xs text-blue-600 mt-1 flex items-center">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                  Quando uma pessoa é selecionada, a quantidade fica fixada em 1 unidade
                </p>
              )}
              {pessoasDisponiveis.length === 0 && !loadingPessoasItem && (
                <p className="text-xs text-yellow-600 mt-1">
                  Nenhuma pessoa disponível para este tipo de produto
                </p>
              )}
            </div>
          )}

          {/* Campos específicos para Matrícula - mostrar apenas se for requerimento de matrícula */}
          {isMatricula && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center mb-3">
                <FontAwesomeIcon icon={faUser} className="text-blue-600 mr-2" />
                <h4 className="text-sm font-semibold text-blue-800">Dados da Matrícula</h4>
              </div>
              
              {/* Campo para selecionar curso */}
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  Curso *
                </label>
                <select
                  value={formItem.curso || ''}
                  onChange={(e) => {
                    const cursoId = e.target.value;
                    setFormItem({...formItem, curso: cursoId});
                    validateField('curso', cursoId);
                  }}
                  disabled={loadingCursos || cursosDisponiveis.length === 0}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                    validationErrors.curso 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-blue-300 hover:border-blue-400 bg-white'
                  } ${loadingCursos ? 'bg-gray-50 cursor-wait' : ''}`}
                >
                  <option value="">
                    {loadingCursos ? 'Carregando cursos...' : 'Selecione o curso'}
                  </option>
                  {cursosDisponiveis.map(curso => (
                    <option key={curso.idCurso} value={curso.idCurso}>
                      {curso.nome}
                    </option>
                  ))}
                </select>
                <ValidationError error={validationErrors.curso} />
              </div>

              {/* Campos para tipo de curso e período */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Tipo de Curso *
                  </label>
                  <select
                    value={formItem.tipoCurso || ''}
                    onChange={(e) => {
                      const tipoCurso = e.target.value;
                      setFormItem({...formItem, tipoCurso});
                      validateField('tipoCurso', tipoCurso);
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                      validationErrors.tipoCurso 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-blue-300 hover:border-blue-400 bg-white'
                    }`}
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="Presencial">Presencial</option>
                    <option value="Distância">Distância</option>
                  </select>
                  <ValidationError error={validationErrors.tipoCurso} />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Período *
                  </label>
                  <select
                    value={formItem.periodo || ''}
                    onChange={(e) => {
                      const periodo = e.target.value;
                      setFormItem({...formItem, periodo});
                      validateField('periodo', periodo);
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                      validationErrors.periodo 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-blue-300 hover:border-blue-400 bg-white'
                    }`}
                  >
                    <option value="">Selecione o período</option>
                    <option value="Semanal">Semanal</option>
                    <option value="Aos Sábados">Aos Sábados</option>
                  </select>
                  <ValidationError error={validationErrors.periodo} />
                </div>
              </div>

              {/* Mensagem informativa */}
              <p className="text-xs text-blue-600 flex items-center">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                Estes campos são obrigatórios para requerimentos de matrícula
              </p>
            </div>
          )}

          {/* Checkbox Troca por Livros - permite valor 0 para este item específico */}
          <div className="flex items-center space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id={`troca-livros-${index}`}
              checked={!!formItem.trocaPorLivros}
              onChange={(e) => {
                const isTroca = e.target.checked;
                setFormItem({
                  ...formItem, 
                  trocaPorLivros: isTroca,
                  // Se marcou como troca, zerar o valor
                  valorUnitario: isTroca ? 0 : formItem.valorUnitario
                });
                // Limpar erro de valor unitário se for troca
                if (isTroca) {
                  setValidationErrors(prev => {
                    const newErrors = {...prev};
                    delete newErrors.valorUnitario;
                    return newErrors;
                  });
                }
              }}
              className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500 cursor-pointer"
            />
            <div className="flex-1">
              <label htmlFor={`troca-livros-${index}`} className="text-sm font-medium text-amber-800 cursor-pointer">
                📚 Troca por Livros
              </label>
              <p className="text-xs text-amber-600">
                Marque se este item é uma troca por livros (valor será R$ 0,00)
              </p>
            </div>
            {formItem.trocaPorLivros && (
              <span className="bg-amber-200 text-amber-800 text-xs font-medium px-2 py-1 rounded">
                Valor zerado
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Detalhamento
            </label>
            <textarea
              ref={observacaoInputRef}
              value={formItem.observacao || formItem.detalhamento || ''}
              onChange={(e) => setFormItem({...formItem, observacao: e.target.value, detalhamento: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder="Detalhes do item..."
              onKeyDown={(e) => {
                // Ctrl+Enter para salvar
                if (e.ctrlKey && e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
          </div>

          <div className="flex flex-row justify-center items-center space-x-2 sm:flex-row sm:justify-between sm:items-center sm:gap-0">
            <div className="text-xs text-gray-500 order-2 sm:order-1">
              <span className="hidden sm:inline">💾 Ctrl+S: Salvar • 🚀 Ctrl+Enter: Salvar e adicionar outro</span>
              <span className="sm:hidden">💾 Ctrl+S: Salvar</span>
            </div>
            <div className="flex flex-row space-x-2 sm:flex-row sm:space-y-0 sm:space-x-2 order-1 sm:order-2">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:w-auto px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-md hover:bg-gray-50"
                title="Fechar edição do item"
              >
                <FontAwesomeIcon icon={faTimes} className="sm:mr-1" />
                <span className="hidden sm:inline">Fechar</span>
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="flex-1 sm:w-auto px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                title="Salvar"
              >
                <FontAwesomeIcon icon={faSave} className="sm:mr-1" />
                <span className="hidden sm:inline">Salvar</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="hover:bg-gray-50 rounded transition-colors">
          {/* Layout responsivo - vertical no mobile, horizontal no desktop */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 px-2 sm:py-1 gap-2 sm:gap-0">
            {/* Produto e Tipo */}
            <div className="flex-1 min-w-0 sm:mr-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
                <span className="font-medium text-gray-900 text-sm truncate">
                  {produtoNome || item.produtoNome || item.nome || 'Produto não selecionado'}
                </span>
                <span className="hidden sm:inline text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-500 truncate">
                  {(() => {
                    const tipoEncontrado = tipos.find(t => t.idTipoProduto == (item.tipoProduto || item.idTipoProduto));
                    return tipoEncontrado?.nome || item.tipoProdutoNome || item.tipoNome || 'Tipo não definido';
                  })()}
                </span>
              </div>
            </div>
            
            {/* Dados numéricos - ajustados para mobile */}
            <div className="flex items-center justify-between sm:justify-start sm:space-x-2 text-xs">
              {/* Badge de Troca por Livros */}
              {item.trocaPorLivros && (
                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium mr-2" title="Item de troca por livros">
                  📚 Troca
                </span>
              )}
              <div className="flex items-center space-x-2">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                  {item.quantidade || 1}x
                </span>
                <span className="text-gray-600 font-medium">
                  {formatCurrency(item.valorUnitario || 0)}
                </span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <span className="text-gray-400 hidden sm:inline">=</span>
                <span className={`px-2 py-0.5 rounded font-bold ${item.trocaPorLivros ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                  {formatCurrency((parseFloat(item.valorUnitario || 0) * parseInt(item.quantidade || 1)))}
                </span>
              </div>
            </div>
            
            {/* Botões de ação - sempre visíveis no mobile */}
            <div className="flex items-center justify-end sm:ml-2 space-x-1">
              {/* Botão para mostrar/ocultar observação */}
              {(item.observacao || item.detalhamento) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowObservacao(!showObservacao);
                  }}
                  className="p-1.5 sm:p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title={showObservacao ? "Ocultar observação" : "Mostrar observação"}
                >
                  <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEdit();
                }}
                className="p-1.5 sm:p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                title="Editar"
              >
                <FontAwesomeIcon icon={faEdit} className="text-xs" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                className="p-1.5 sm:p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                title="Remover"
              >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
              </button>
            </div>
          </div>

          {/* Observações toggleáveis - mais compactas no mobile */}
          {(item.observacao || item.detalhamento) && showObservacao && (
            <div className="px-2 pb-2 animate-fade-in">
              <div className="text-xs text-gray-700 bg-yellow-50 px-2 sm:px-3 py-2 rounded border border-yellow-200 shadow-sm">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faFileAlt} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-yellow-800">Observação:</span>
                    <p className="mt-1 text-gray-700 break-words">{item.observacao || item.detalhamento}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dados da Matrícula - mostrar apenas se for requerimento de matrícula e tiver dados */}
          {((item.curso && item.tipoCurso && item.periodo) || (item.cursoNome)) && (
            <div className="px-2 pb-2 animate-fade-in">
              <div className="text-xs text-gray-700 bg-blue-50 px-2 sm:px-3 py-2 rounded border border-blue-200 shadow-sm">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faUser} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-blue-800">Dados da Matrícula:</span>
                    <div className="mt-1 space-y-1">
                      {(item.cursoNome || item.curso) && (
                        <div className="text-gray-700">
                          <span className="font-medium">Curso:</span> {item.cursoNome || `ID ${item.curso}`}
                        </div>
                      )}
                      {item.tipoCurso && (
                        <div className="text-gray-700">
                          <span className="font-medium">Tipo:</span> {item.tipoCurso}
                        </div>
                      )}
                      {item.periodo && (
                        <div className="text-gray-700">
                          <span className="font-medium">Período:</span> {item.periodo}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Modal de confirmação para exclusão */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja remover este item do pedido?`}
        confirmText="Sim, Remover"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
    </div>
  );
};

const PedidoEdit = () => {
  const { hasPermission, user } = useAuth();
  
  // Verificar se é usuário com nível abaixo de 4 (restrições aplicadas)
  const isRestrictedUser = () => {
    return user && user.nivel_acesso < 4;
  };
  
  // Função para obter classes CSS do campo (com erro ou normal)
  const getFieldClass = (fieldName, fieldErrors, baseClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm") => {
    if (fieldErrors && fieldErrors[fieldName]) {
      // Campo com erro - remover classes normais e adicionar classes de erro
      return baseClass
        .replace(/border-gray-\d+/g, 'border-red-500')
        .replace(/focus:ring-green-\d+/g, 'focus:ring-red-400')
        .replace(/bg-gray-\d+/g, '') + ' bg-red-50';
    }
    // Campo normal
    return baseClass;
  };
  
  // Função para formatação de valores monetários no padrão brasileiro
  const formatCurrency = (value) => {
    const numValue = Number(value) || 0;
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter campo boolean que pode vir como buffer do MySQL
  const convertBooleanField = (value) => {
    if (value && typeof value === 'object' && value.type === 'Buffer') {
      return value.data[0] === 1;
    } else if (typeof value === 'number') {
      return value === 1;
    } else if (typeof value === 'boolean') {
      return value;
    }
    return false;
  };

  const [form, setForm] = useState({
    codigo: '',
    prazoEntrega: '',
    // REMOVIDO: pago e pagoTexto não são editáveis pela interface
    // pago: false,
    // pagoTexto: 'Não',
    extensao: '',
    solicitacaoExtensao: '',
    solicitacaoIbe: '',
    formaEntrega: '',
    formaPagamento: '',
    quemPediu: '',
    atribuirAo: '',
    detalhes: '',
    valor: 0,
    status: '' // Status vazio para novos pedidos - será definido ao salvar
  });

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // Erros de validação por campo
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeletePedidoConfirm, setShowDeletePedidoConfirm] = useState(false);
  const [isDraft, setIsDraft] = useState(false); // Estado para controlar se é modo rascunho
  const [actionType, setActionType] = useState(''); // Estado para controlar tipo de ação: 'draft', 'send', 'normal'
  const [activeTab, setActiveTab] = useState('dados');
  const [pessoasExtensao, setPessoasExtensao] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Controlar carregamento inicial
  const [scrollInfo, setScrollInfo] = useState({ canScrollUp: false, canScrollDown: false }); // Estado do scroll
  const [hasItemInEdit, setHasItemInEdit] = useState(false); // Estado para saber se há item em edição
  const [itemsInEditMode, setItemsInEditMode] = useState(new Set()); // Rastrear quais itens estão em edição
  const [showGlobalItemSuccess, setShowGlobalItemSuccess] = useState(false); // Mensagem global de sucesso para itens
  const isFirstExtensionChange = useRef(true); // Ref para controlar primeira mudança de extensão
  const [showExtensionChangeConfirm, setShowExtensionChangeConfirm] = useState(false); // Modal de confirmação mudança extensão
  const [pendingExtensionChange, setPendingExtensionChange] = useState(null); // Extensão pendente para mudança
  const [showMissingPersonConfirm, setShowMissingPersonConfirm] = useState(false); // Modal para confirmar salvamento com pessoas faltando
  const [pendingSubmitData, setPendingSubmitData] = useState(null); // Dados pendentes para submissão
  
  const [selectOptions, setSelectOptions] = useState({
    formasEntrega: [],
    formasPagamento: [],
    prazosEntrega: [],
    produtos: [],
    tipos: []
  });

  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  // Função para verificar se existem itens com pessoas destinatárias selecionadas
  const hasItemsWithDestinationPerson = () => {
    return itens.some(item => item.pessoaDestinataria && item.pessoaDestinataria !== '');
  };

  // Função para limpar pessoas destinatárias de todos os itens
  const clearAllDestinationPersons = () => {
    setItens(prev => prev.map(item => ({
      ...item,
      pessoaDestinataria: ''
    })));
  };

  // Função para verificar itens que precisam de pessoa destinatária mas não têm
  const getItemsMissingDestinationPerson = () => {
    return itens.filter(item => {
      // Verificar se o tipo de produto permite/requer pessoa destinatária
      const tipoEncontrado = selectOptions.tipos?.find(t => t.idTipoProduto == (item.tipoProduto || item.idTipoProduto));
      const tipoPermitePessoa = tipoEncontrado?.permiteVinculoPessoa === 1;
      
      // Se o tipo permite pessoa destinatária mas o item não tem pessoa selecionada
      return tipoPermitePessoa && (!item.pessoaDestinataria || item.pessoaDestinataria === '');
    });
  };

  // Função para atualizar itens sem pessoa destinatária para null no banco
  const updateItemsWithNullPerson = async () => {
    const itemsSemPessoa = getItemsMissingDestinationPerson();
    
    if (!isNew && itemsSemPessoa.length > 0) {
      try {
        // Atualizar cada item sem pessoa destinatária no servidor
        for (const item of itemsSemPessoa) {
          if (!item.isNew) { // Apenas itens que já existem no banco
            const itemPayload = {
              pedidoId: id,
              tipoProduto: item.tipoProduto || item.idTipoProduto,
              produto: item.produto || item.idProduto,
              quantidade: item.quantidade,
              valorUnitario: item.valorUnitario,
              detalhamento: item.detalhamento || item.observacao,
              pessoa: null // Definir explicitamente como null
            };
            
            console.log('Atualizando item sem pessoa destinatária:', {
              itemId: item.idItemPedido,
              payload: itemPayload
            });
            
            await pedidoService.updateItem(id, item.idItemPedido, itemPayload);
          }
        }
        
        // Atualizar o estado local para refletir as mudanças
        setItens(prev => prev.map(item => {
          const tipoEncontrado = selectOptions.tipos?.find(t => t.idTipoProduto == (item.tipoProduto || item.idTipoProduto));
          const tipoPermitePessoa = tipoEncontrado?.permiteVinculoPessoa === 1;
          
          if (tipoPermitePessoa && (!item.pessoaDestinataria || item.pessoaDestinataria === '')) {
            console.log('Limpando pessoa destinatária do item local:', item);
            return { ...item, pessoaDestinataria: null };
          }
          return item;
        }));
        
        console.log(`${itemsSemPessoa.length} itens atualizados com pessoa = null`);
        
      } catch (error) {
        console.error('Erro ao atualizar itens com pessoa null:', error);
        throw new Error('Erro ao atualizar itens sem pessoa destinatária');
      }
    } else {
      console.log('Nenhum item precisa de atualização para pessoa = null');
    }
  };

  const loadExtensoes = useCallback(async () => {
    try {
      const response = await extensaoService.list({ limit: 1000 }); // Buscar todas as extensões
      if (response.success) {
        setExtensoes(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar extensões:', error);
      setError('Erro ao carregar extensões');
    }
  }, []);

  const loadFuncionarios = useCallback(async () => {
    setLoadingFuncionarios(true);
    try {
      console.log('Carregando funcionários...');
      const response = await pessoaService.getFuncionarios();
      console.log('Funcionários carregados:', response);
      if (response.success) {
        setFuncionarios(response.data || []);
      } else {
        console.error('Erro ao carregar funcionários:', response.error);
        setFuncionarios([]);
      }
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      setFuncionarios([]);
    } finally {
      setLoadingFuncionarios(false);
    }
  }, []);

  const loadSelectOptions = useCallback(async () => {
    try {
      const [pedidoResponse, formasEntregaResponse, prazosEntregaResponse, formasPagamentoResponse, tiposResponse] = await Promise.all([
        pedidoService.getSelectOptions(),
        formaEntregaService.getAll(),
        prazoEntregaService.getAll(),
        formaPagamentoService.getAll(),
        tipoProdutoService.getAll()
      ]);

      console.log('Opções de pedido:', pedidoResponse);
      console.log('Formas de Entrega:', formasEntregaResponse);
      console.log('Prazos de Entrega:', prazosEntregaResponse);
      console.log('Formas de Pagamento:', formasPagamentoResponse);
      console.log('Tipos de Produto:', tiposResponse);

      const selectData = {
        ...pedidoResponse.success ? pedidoResponse.data : {},
        formasEntrega: formasEntregaResponse.success ? formasEntregaResponse.data : [],
        prazosEntrega: prazosEntregaResponse.success ? prazosEntregaResponse.data : [],
        formasPagamento: formasPagamentoResponse.success ? formasPagamentoResponse.data : [],
        tipos: tiposResponse.success ? tiposResponse.data : []
      };
      
      setSelectOptions(selectData);
    } catch (err) {
      console.error('Erro ao carregar opções:', err);
    }
  }, []);

  const loadPessoasExtensao = useCallback(async (extensaoId) => {
    console.log('=== loadPessoasExtensao chamado ===');
    console.log('extensaoId:', extensaoId);
    console.log('isNew:', isNew);
    setLoadingPessoas(true);
    try {
      const response = await pessoaService.getByExtensao(extensaoId);
      if (response.success) {
        console.log('Pessoas carregadas:', response.data);
        setPessoasExtensao(response.data || []);
      } else {
        console.error('Erro ao carregar pessoas da extensão:', response.error);
        setPessoasExtensao([]);
        setError('Erro ao carregar pessoas da extensão');
      }
    } catch (error) {
      console.error('Erro ao carregar pessoas da extensão:', error);
      setPessoasExtensao([]);
      setError('Erro ao carregar pessoas da extensão');
    } finally {
      setLoadingPessoas(false);
    }
  }, []);

  const loadPedido = useCallback(async () => {
    try {
      // Carregar dados básicos primeiro
      await Promise.all([
        loadSelectOptions(),
        loadExtensoes(),
        loadFuncionarios()
      ]);
      
      const result = await pedidoService.getById(id);
      console.log('🔍 PEDIDO CARREGADO - Debug completo:', result);
      console.log('🔍 Dados originais do backend:');
      console.log('  - data.pago (original):', result.data?.pago);
      console.log('  - data.pagoTexto (original):', result.data?.pagoTexto);
      
      if (result.success) {
        const data = result.data;
        
        // Converter campo boolean do MySQL
        data.pago = convertBooleanField(data.pago);
        console.log('🔍 Após conversão boolean:');
        console.log('  - data.pago (convertido):', data.pago, 'tipo:', typeof data.pago);
        console.log('  - data.pagoTexto (inalterado):', data.pagoTexto);
        
        const formData = {
          codigo: data.idProduto || '',
          prazoEntrega: data.prazoEntrega?.toString() || '3',
          // INCLUIR campos de pagamento apenas para leitura (não serão editados)
          pago: data.pago,
          pagoTexto: data.pagoTexto || 'Não',
          extensao: data.extensao?.toString() || '',
          status: data.status || 'Pendente',
          solicitacaoExtensao: data.solicitadoExtensaoEm ? data.solicitadoExtensaoEm.split('T')[0] : '',
          formaEntrega: data.formaEntrega?.toString() || '',
          formaPagamento: data.formaPagamento?.toString() || '',
          quemPediu: data.pessoa?.toString() || '',
          atribuirAo: data.user || '',
          solicitacaoIbe: data.solicitadoIbeEm ? data.solicitadoIbeEm.split('T')[0] : '',
          valor: data.valor || 0
        };
        
        console.log('📋 Dados de pagamento carregados (somente leitura):');
        console.log('  - pago:', formData.pago);
        console.log('  - pagoTexto:', formData.pagoTexto);
        
        setForm(formData);
        
        // Verificar se o usuário pode editar pedidos com status restrito
        const statusRestrito = ['Pendente', 'Concluido', 'Cancelado'].includes(data.status);
        if (statusRestrito && user?.nivel_acesso < 4) {
          const statusMsg = {
            'Pendente': 'Pendente',
            'Concluido': 'Concluído',
            'Cancelado': 'Cancelado'
          }[data.status] || data.status;
          toast.error(`Apenas administradores podem editar pedidos com status ${statusMsg}.`);
          navigate('/pedidos', { replace: true });
          return;
        }
        
        // Se há uma extensão, carregar as pessoas dessa extensão
        if (formData.extensao) {
          await loadPessoasExtensao(formData.extensao);
        }
        
        // Carregar itens do pedido
        await loadItensPedido(id);
      } else {
        setError(result.error || 'Erro ao carregar pedido');
      }
    } catch (err) {
      setError('Erro ao carregar pedido');
      console.error('Erro:', err);
    }
  }, [id, loadSelectOptions, loadExtensoes, loadFuncionarios, loadPessoasExtensao]);

  const loadItensPedido = useCallback(async (pedidoId) => {
    try {
      const response = await pedidoService.getItens(pedidoId);
      console.log('📦 Itens do pedido carregados do backend:', response);
      
      if (response.success) {
        // Mapear e garantir que valorUnitario está presente
        const itensMapeados = (response.data || []).map(item => {
          console.log('🔍 Item original do backend:', item);
          console.log('  - valorUnitario:', item.valorUnitario, 'tipo:', typeof item.valorUnitario);
          console.log('  - valor:', item.valor, 'tipo:', typeof item.valor);
          console.log('  - quantidade:', item.quantidade);
          
          // Garantir que valorUnitario existe e não é zero indevidamente
          // Se valorUnitario for null/undefined mas valor existir, calcular valorUnitario
          let valorUnitarioFinal = item.valorUnitario;
          if ((!valorUnitarioFinal || valorUnitarioFinal === 0) && item.valor && item.quantidade) {
            valorUnitarioFinal = parseFloat(item.valor) / parseInt(item.quantidade);
            console.log('  ⚠️ valorUnitario estava zerado/null, calculado a partir de valor/quantidade:', valorUnitarioFinal);
          }
          
          const itemMapeado = {
            ...item,
            pessoaDestinataria: item.pessoa, // Mapear pessoa para pessoaDestinataria
            valorUnitario: valorUnitarioFinal || 0, // Garantir que sempre tenha um valor
            trocaPorLivros: !!item.trocaPorLivros // Garantir que o campo de troca por livros seja booleano
          };
          
          console.log('✅ Item mapeado final:', itemMapeado);
          console.log('  - valorUnitario final:', itemMapeado.valorUnitario);
          console.log('  - trocaPorLivros:', itemMapeado.trocaPorLivros);
          
          return itemMapeado;
        });
        
        setItens(itensMapeados);
        // Limpar rastreamento de itens em edição ao recarregar
        setItemsInEditMode(new Set());
      } else {
        console.error('Erro ao carregar itens do pedido:', response.error);
      }
    } catch (error) {
      console.error('Erro ao carregar itens do pedido:', error);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!isNew) {
        await loadPedido();
      } else {
        // Apenas para novos pedidos, carregar dados básicos
        loadSelectOptions();
        loadExtensoes();
        loadFuncionarios();
        
        // Para usuários restritos (nível < 4), definir datas automaticamente na criação
        if (isRestrictedUser()) {
          const today = new Date().toISOString().split('T')[0];
          setForm(prev => ({
            ...prev,
            solicitacaoExtensao: today,
            solicitacaoIbe: today
          }));
        }
      }
      setLoadingData(false);
      setIsInitialLoad(false); // Marcar que o carregamento inicial terminou
    };
    
    loadInitialData();
  }, [isNew, loadPedido, loadSelectOptions, loadExtensoes, loadFuncionarios]);

  // Carregar pessoas quando a extensão mudar (para novos pedidos e edição)
  useEffect(() => {
    console.log('=== useEffect extensão mudou ===');
    console.log('form.extensao:', form.extensao);
    console.log('isNew:', isNew);
    console.log('isInitialLoad:', isInitialLoad);
    console.log('isFirstExtensionChange.current:', isFirstExtensionChange.current);
    console.log('loadingData:', loadingData);
    
    // Evitar carregar durante o carregamento inicial dos dados
    if (form.extensao && !loadingData) {
      loadPessoasExtensao(form.extensao);
      
      // Limpar o campo "quemPediu" apenas quando é uma mudança manual (não na primeira mudança que é o carregamento)
      if (!isNew && !isFirstExtensionChange.current) { 
        console.log('Limpando quemPediu na edição (mudança manual)');
        setForm(prev => ({ ...prev, quemPediu: '' }));
      }
      
      // Marcar que já houve a primeira mudança
      if (isFirstExtensionChange.current) {
        isFirstExtensionChange.current = false;
      }
    }
    console.log('===============================');
  }, [form.extensao, loadPessoasExtensao, isNew, loadingData, isInitialLoad]);

  // Monitorar mudanças nos campos de pagamento para detectar alterações indevidas
  useEffect(() => {
    console.log('🔍 MONITOR PAGAMENTO - Estado atual:');
    console.log('  - form.pago:', form.pago);
    console.log('  - form.pagoTexto:', form.pagoTexto);
  }, [form.pago, form.pagoTexto]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // Verificar mudança de extensão
    if (name === 'extensao' && value !== form.extensao) {
      // Se há itens com pessoas destinatárias, mostrar confirmação
      if (hasItemsWithDestinationPerson()) {
        setPendingExtensionChange(value);
        setShowExtensionChangeConfirm(true);
        return; // Não aplicar a mudança ainda
      }
    }
    
    setForm(prev => ({
      ...prev,
      [name]: newValue
    }));
    setError('');
    
    // Limpar erro do campo específico quando o usuário editar
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Função para confirmar mudança de extensão
  const confirmExtensionChange = () => {
    if (pendingExtensionChange !== null) {
      // Limpar pessoas destinatárias de todos os itens
      clearAllDestinationPersons();
      
      // Aplicar a mudança de extensão
      setForm(prev => ({
        ...prev,
        extensao: pendingExtensionChange
      }));
      
      // Limpar estados do modal
      setShowExtensionChangeConfirm(false);
      setPendingExtensionChange(null);
    }
  };

  // Função para cancelar mudança de extensão
  const cancelExtensionChange = () => {
    setShowExtensionChangeConfirm(false);
    setPendingExtensionChange(null);
  };

  // Função para confirmar salvamento com pessoas destinatárias faltando
  const confirmSubmitWithMissingPersons = async () => {
    setShowMissingPersonConfirm(false);
    
    if (pendingSubmitData) {
      await proceedWithSubmit(pendingSubmitData.isDraftSave);
      setPendingSubmitData(null);
    }
  };

  // Função para cancelar salvamento com pessoas destinatárias faltando
  const cancelSubmitWithMissingPersons = () => {
    setShowMissingPersonConfirm(false);
    setPendingSubmitData(null);
    setLoading(false);
  };

  const addItem = async () => {
    // Sempre adicionar item localmente primeiro para preenchimento
    setItens(prev => [...prev, {
      tipoProduto: '',
      produto: '',
      valorUnitario: '',
      quantidade: 1,
      detalhamento: '',
      pessoaDestinataria: '', // Campo para pessoa destinatária
      trocaPorLivros: false, // Campo para troca por livros
      curso: null, // Campo para matrícula
      tipoCurso: null, // Campo para matrícula
      periodo: null, // Campo para matrícula
      isNew: true // Marca como novo item local
    }]);
    
    // Scroll automático para o novo item após um pequeno delay
    setTimeout(() => {
      const itemsContainer = document.querySelector('.items-container');
      if (itemsContainer) {
        itemsContainer.scrollTo({
          top: itemsContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Função para controlar o estado do scroll
  const handleScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    setScrollInfo({
      canScrollUp: scrollTop > 0,
      canScrollDown: scrollTop < scrollHeight - clientHeight - 1
    });
  };

  const updateItem = async (index, updatedItem) => {
    console.log('=== DEBUG updateItem ===');
    console.log('index:', index);
    console.log('updatedItem:', updatedItem);
    console.log('updatedItem.produto:', updatedItem.produto);
    console.log('updatedItem.isNew:', updatedItem.isNew);
    console.log('Lista completa de itens:', itens);
    console.log('isNew (pedido novo?):', isNew);
    console.log('========================');
    
    // 🔍 Verificar se o tipo do produto é "Requerimento"
    // Usar selectOptions.tipos que contém os tipos de produto carregados
    const tipoEncontrado = selectOptions.tipos?.find(t => t.idTipoProduto == updatedItem.tipoProduto);
    const isRequerimento = tipoEncontrado?.nome?.toLowerCase().includes('requerimento') || false;
    
    console.log('🔍 Tipo do produto:', tipoEncontrado?.nome || 'não encontrado');
    console.log('🔍 É Requerimento?', isRequerimento);
    
    // 🔍 VALIDAÇÃO DE DUPLICIDADE - EXECUTAR SEMPRE, ANTES DE QUALQUER OUTRA AÇÃO
    // Para Requerimentos, NÃO fazer validação de duplicidade (cada requerimento é individual)
    let indexItemDuplicado = -1;
    
    if (!isRequerimento) {
      console.log('🔍 Verificando duplicatas (não é requerimento)...');
      indexItemDuplicado = itens.findIndex((item, i) => {
        const isDuplicate = i !== index && item.produto === updatedItem.produto;
        console.log(`Comparando item ${i}: produto=${item.produto}, updatedItem.produto=${updatedItem.produto}, isDuplicate=${isDuplicate}`);
        return isDuplicate;
      });
      console.log('Index do item duplicado encontrado:', indexItemDuplicado);
    } else {
      console.log('⚠️ Produto é Requerimento - pulando verificação de duplicidade (cada requerimento é individual)');
    }
    
    // Se encontrou duplicata E não é requerimento, somar quantidades
    if (indexItemDuplicado !== -1) {
      const itemExistente = itens[indexItemDuplicado];
      const produtoNome = updatedItem.produtoNome || 'Este produto';
      const novaQuantidade = parseInt(itemExistente.quantidade || 1) + parseInt(updatedItem.quantidade || 1);
      
      console.log(`🔄 DUPLICATA DETECTADA!`);
      console.log(`Produto: ${produtoNome}`);
      console.log(`Quantidade existente: ${itemExistente.quantidade}`);
      console.log(`Quantidade a adicionar: ${updatedItem.quantidade}`);
      console.log(`Nova quantidade total: ${novaQuantidade}`);
      
      // Remover o item duplicado novo da lista
      setItens(prev => prev.filter((_, i) => i !== index));
      
      // Atualizar o item existente com a soma das quantidades
      if (isNew || itemExistente.isNew) {
        // Pedido novo OU item existente novo: atualizar apenas localmente
        console.log('📝 Atualizando quantidade localmente');
        setItens(prev => prev.map((item, i) => 
          i === indexItemDuplicado 
            ? { ...item, quantidade: novaQuantidade }
            : item
        ));
        
        setError('');
        setSuccessMessage(`${produtoNome}: Quantidade atualizada para ${novaQuantidade} unidades.`);
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 5000);
      } else {
        // Pedido salvo E item salvo: atualizar no servidor
        console.log('💾 Atualizando quantidade no servidor');
        try {
          const updatePayload = {
            tipoProduto: itemExistente.tipoProduto,
            produto: itemExistente.produto,
            quantidade: novaQuantidade,
            valorUnitario: itemExistente.valorUnitario,
            detalhamento: itemExistente.detalhamento || itemExistente.observacao,
            pessoa: itemExistente.pessoaDestinataria,
            curso: itemExistente.curso || null,
            tipoCurso: itemExistente.tipoCurso || null,
            periodo: itemExistente.periodo || null
          };
          
          const response = await pedidoService.updateItem(id, itemExistente.idItemPedido, updatePayload);
          
          if (response.success) {
            await loadItensPedido(id);
            setTimeout(() => forceUpdateTotal(), 300);
            
            setError('');
            setSuccessMessage(`${produtoNome}: Quantidade atualizada de ${itemExistente.quantidade} para ${novaQuantidade} unidades.`);
            setSuccess(true);
            setTimeout(() => {
              setSuccess(false);
              setSuccessMessage('');
            }, 5000);
          } else {
            setError(`Erro ao atualizar quantidade: ${response.error || 'Erro desconhecido'}`);
          }
        } catch (updateError) {
          console.error('Erro ao atualizar item existente:', updateError);
          setError(`Erro ao atualizar quantidade: ${updateError.message}`);
        }
      }
      
      return; // Interromper execução após tratar duplicata
    }
    
    // Nenhuma duplicata encontrada - continuar com a lógica normal
    console.log('✅ Nenhuma duplicata - prosseguindo com operação normal');
    
    if (isNew) {
      // Para pedidos novos, atualizar apenas localmente
      setItens(prev => prev.map((item, i) => i === index ? updatedItem : item));
    } else if (updatedItem.isNew) {
      // Para itens novos em pedidos existentes, criar no servidor
      try {
        console.log('=== DEBUG FRONTEND createItem ===');
        console.log('updatedItem:', updatedItem);
        
        // Validações básicas antes de enviar
        if (!updatedItem.tipoProduto || !updatedItem.produto) {
          setError('Erro: Tipo de produto e produto são obrigatórios');
          setItens(prev => prev.filter((_, i) => i !== index));
          return;
        }
        
        if (!updatedItem.quantidade || updatedItem.quantidade <= 0) {
          setError('Erro: Quantidade deve ser maior que zero');
          setItens(prev => prev.filter((_, i) => i !== index));
          return;
        }
        
        // Verificar se forma de pagamento é "Troca por Livros" (permite valor zero) OU se o item está marcado como troca
        const formaPagamentoSelecionada = selectOptions.formasPagamento?.find(
          fp => String(fp.idFormaPagamento) === String(form.formaPagamento)
        );
        const isTrocaPorLivros = formaPagamentoSelecionada?.nome?.toLowerCase().includes('troca por livros');
        
        // Validar valor: se for "Troca por Livros" ou item marcado como troca, permite valor 0; caso contrário, deve ser > 0
        if (isTrocaPorLivros || updatedItem.trocaPorLivros) {
          if (updatedItem.valorUnitario === undefined || updatedItem.valorUnitario === null || updatedItem.valorUnitario < 0) {
            setError('Erro: Valor unitário não pode ser negativo');
            setItens(prev => prev.filter((_, i) => i !== index));
            return;
          }
        } else {
          if (!updatedItem.valorUnitario || updatedItem.valorUnitario <= 0) {
            setError('Erro: Valor unitário deve ser maior que zero');
            setItens(prev => prev.filter((_, i) => i !== index));
            return;
          }
        }
        
        // A validação de duplicidade já foi feita no início da função updateItem
        
        const itemPayload = {
          pedidoId: id,
          tipoProduto: updatedItem.tipoProduto,
          produto: updatedItem.produto,
          quantidade: updatedItem.quantidade,
          valorUnitario: updatedItem.valorUnitario, // Corrigido: usar valorUnitario, não valor
          detalhamento: updatedItem.detalhamento || updatedItem.observacao,
          pessoa: updatedItem.pessoaDestinataria, // Incluir pessoa destinatária
          // Campo de troca por livros
          trocaPorLivros: !!updatedItem.trocaPorLivros,
          // Campos específicos para matrícula
          curso: updatedItem.curso || null,
          tipoCurso: updatedItem.tipoCurso || null,
          periodo: updatedItem.periodo || null
          // Removido cadastradoPor - será definido no backend com o usuário logado
        };
        
        console.log('=== DEBUG CREATE ITEM ===');
        console.log('updatedItem COMPLETO:', JSON.stringify(updatedItem, null, 2));
        console.log('updatedItem.trocaPorLivros:', updatedItem.trocaPorLivros);
        console.log('itemPayload enviado:', JSON.stringify(itemPayload, null, 2));
        console.log('itemPayload.trocaPorLivros:', itemPayload.trocaPorLivros);
        console.log('=========================');
        
        const response = await pedidoService.createItem(id, itemPayload);

        if (response.success) {
          // Recarregar itens do servidor para refletir o novo item criado
          await loadItensPedido(id);
          
          // Forçar atualização do total como backup
          setTimeout(() => forceUpdateTotal(), 300);
        } else {
          console.error('Erro do servidor ao criar item:', response.error);
          // Remover o item que falhou da lista local
          setItens(prev => prev.filter((_, i) => i !== index));
          // Mostrar mensagem de erro específica
          setError(`Erro ao adicionar item: ${response.error || 'Erro desconhecido'}`);
        }
      } catch (error) {
        console.error('Erro ao criar item:', error);
        // Remover o item que falhou da lista local
        setItens(prev => prev.filter((_, i) => i !== index));
        
        // Extrair mensagem de erro mais específica
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Erro de comunicação com o servidor';
        
        // Se é erro 400, é um erro de validação de negócio (ex: estoque)
        const isBusinessError = error.response?.status === 400;
        const errorPrefix = isBusinessError ? '' : 'Erro ao adicionar item: ';
        
        setError(`${errorPrefix}${errorMessage}`);
        
        // Auto-scroll para a mensagem de erro
        setTimeout(() => {
          const errorElement = document.querySelector('.bg-red-50');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } else {
      // Para itens existentes, atualizar no servidor
      try {
        console.log('=== DEBUG FRONTEND updateItem ===');
        console.log('updatedItem:', updatedItem);
        
        // Validações básicas antes de enviar
        if (!updatedItem.tipoProduto || !updatedItem.produto) {
          setError('Erro: Tipo de produto e produto são obrigatórios');
          return;
        }
        
        if (!updatedItem.quantidade || updatedItem.quantidade <= 0) {
          setError('Erro: Quantidade deve ser maior que zero');
          return;
        }
        
        // Verificar se forma de pagamento é "Troca por Livros" (permite valor zero) OU se o item está marcado como troca
        const formaPagamentoSelecionada = selectOptions.formasPagamento?.find(
          fp => String(fp.idFormaPagamento) === String(form.formaPagamento)
        );
        const isTrocaPorLivros = formaPagamentoSelecionada?.nome?.toLowerCase().includes('troca por livros');
        
        // Validar valor: se for "Troca por Livros" ou item marcado como troca, permite valor 0; caso contrário, deve ser > 0
        if (isTrocaPorLivros || updatedItem.trocaPorLivros) {
          if (updatedItem.valorUnitario === undefined || updatedItem.valorUnitario === null || updatedItem.valorUnitario < 0) {
            setError('Erro: Valor unitário não pode ser negativo');
            return;
          }
        } else {
          if (!updatedItem.valorUnitario || updatedItem.valorUnitario <= 0) {
            setError('Erro: Valor unitário deve ser maior que zero');
            return;
          }
        }
                // 🔍 VALIDAÇÃO DE DUPLICIDADE: Verificar se está mudando para um produto que já existe
        const itemDuplicado = itens.find((item, i) => 
          i !== index && 
          item.produto === updatedItem.produto &&
          item.idItemPedido !== updatedItem.idItemPedido // Excluir o próprio item
        );
        
        if (itemDuplicado) {
          const produtoNome = updatedItem.produtoNome || 'Este produto';
          setError(`${produtoNome} já está incluído neste pedido. Para alterar a quantidade, edite o item existente.`);
          
          // Auto-scroll para a mensagem de erro
          setTimeout(() => {
            const errorElement = document.querySelector('.bg-red-50');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
        
        const updatePayload = {
          tipoProduto: updatedItem.tipoProduto,
          produto: updatedItem.produto,
          quantidade: updatedItem.quantidade,
          valorUnitario: updatedItem.valorUnitario,
          detalhamento: updatedItem.detalhamento || updatedItem.observacao,
          pessoa: updatedItem.pessoaDestinataria, // Incluir pessoa destinatária
          // Campo de troca por livros
          trocaPorLivros: !!updatedItem.trocaPorLivros,
          // Campos específicos para matrícula
          curso: updatedItem.curso || null,
          tipoCurso: updatedItem.tipoCurso || null,
          periodo: updatedItem.periodo || null
          // Removido alteradoPor - será definido no backend com o usuário logado
        };
        
        console.log('updatePayload enviado:', updatePayload);
        console.log('pessoaDestinataria no updatedItem:', updatedItem.pessoaDestinataria);
        console.log('pessoa no payload:', updatePayload.pessoa);
        console.log('===================================');
        
        const response = await pedidoService.updateItem(id, updatedItem.idItemPedido, updatePayload);

        if (response.success) {
          // Recarregar itens do servidor
          await loadItensPedido(id);
          
          // Forçar atualização do total como backup
          setTimeout(() => forceUpdateTotal(), 300);
        } else {
          console.error('Erro do servidor ao atualizar item:', response.error);
          // Mostrar mensagem de erro específica
          setError(`Erro ao atualizar item: ${response.error || 'Erro desconhecido'}`);
        }
      } catch (error) {
        console.error('Erro ao atualizar item:', error);
        
        // Extrair mensagem de erro mais específica
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Erro de comunicação com o servidor';
        
        // Se é erro 400, é um erro de validação de negócio (ex: estoque)
        const isBusinessError = error.response?.status === 400;
        const errorPrefix = isBusinessError ? '' : 'Erro ao atualizar item: ';
        
        setError(`${errorPrefix}${errorMessage}`);
        
        // Auto-scroll para a mensagem de erro
        setTimeout(() => {
          const errorElement = document.querySelector('.bg-red-50');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  };

  const removeItem = async (index) => {
    const item = itens[index];
    
    // Limpar do rastreamento de modo de edição
    setItemsInEditMode(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    
    if (isNew || item.isNew) {
      // Para pedidos novos ou itens novos, remover apenas localmente
      setItens(prev => prev.filter((_, i) => i !== index));
    } else {
      // Para itens existentes, remover do servidor
      try {
        const response = await pedidoService.deleteItem(id, item.idItemPedido);

        if (response.success) {
          // Recarregar itens do servidor
          await loadItensPedido(id);
          
          // Forçar atualização do total como backup
          setTimeout(() => forceUpdateTotal(), 300);
        } else {
          setError('Erro ao remover item: ' + response.error);
        }
      } catch (error) {
        console.error('Erro ao remover item:', error);
        setError('Erro ao remover item');
      }
    }
  };

  const calculateTotal = () => {
    const total = itens.reduce((total, item) => {
      // Suporta tanto campos locais quanto da API
      const quantidade = item.quantidade || 0;
      // Priorizar valorUnitario que é o campo correto, usar valor apenas como fallback
      const valorUnitario = item.valorUnitario || item.valor || 0;
      const subtotal = quantidade * valorUnitario;
      
      console.log(`Item ${item.idItemPedido || 'novo'}: ${quantidade}x R$${valorUnitario} = R$${subtotal}`);
      
      return total + subtotal;
    }, 0);
    
    console.log(`TOTAL CALCULADO: R$${total} (baseado em ${itens.length} itens)`);
    return total;
  };

  const calculateTotalQuantity = () => {
    return itens.reduce((total, item) => {
      const quantidade = item.quantidade || 0;
      return total + quantidade;
    }, 0);
  };

  // Atualizar o valor total do pedido sempre que os itens mudarem
  useEffect(() => {
    const novoTotal = calculateTotal();
    console.log('=== DEBUG VALOR TOTAL ===');
    console.log('Itens atuais:', itens);
    console.log('Valor total calculado:', novoTotal);
    console.log('Valor atual no form:', form.valor);
    console.log('========================');
    
    setForm(prev => ({
      ...prev,
      valor: novoTotal
    }));
  }, [itens]);

  // UseEffect para atualizar valor total no backend quando itens mudarem (apenas para pedidos existentes)
  useEffect(() => {
    const updateTotalInBackend = async () => {
      if (!isNew && itens.length >= 0) { // Permitir mesmo com 0 itens (pedido vazio)
        const novoTotal = calculateTotal();
        
        console.log('=== DEBUG USEEFFECT TOTAL ===');
        console.log('isNew:', isNew);
        console.log('itens.length:', itens.length);
        console.log('form.valor atual:', form.valor);
        console.log('novoTotal calculado:', novoTotal);
        console.log('Vai atualizar?', novoTotal !== form.valor);
        console.log('=============================');
        
        // Só atualizar se o valor realmente mudou
        if (Math.abs(novoTotal - (form.valor || 0)) > 0.01) { // Usar tolerância para valores decimais
          console.log('=== ATUALIZANDO TOTAL NO BACKEND ===');
          console.log('Valor anterior:', form.valor);
          console.log('Novo valor:', novoTotal);
          console.log('===================================');
          
          try {
            // CORREÇÃO: Payload com campos essenciais preservados para evitar nulls
            const totalPayload = {
              valor: novoTotal,
              // Preservar campos atuais para evitar nulls no banco
              status: form.status || 'Pendente',
              extensao: form.extensao,
              prazoEntrega: form.prazoEntrega,
              formaEntrega: form.formaEntrega,
              formaPagamento: form.formaPagamento,
              pessoa: form.quemPediu,
              user: form.atribuirAo,
              // REMOVIDO: pago e pagoTexto não devem ser modificados nas atualizações
              solicitadoExtensaoEm: form.solicitacaoExtensao || null,
              solicitadoIbeEm: form.solicitacaoIbe || null,
              detalhes: form.detalhes || null
            };
            
            console.log('📤 Payload do sync automático (com campos preservados):', totalPayload);
            console.log('🔍 Verificação sync automático - Campos de pagamento:');
            console.log('  - pago presente?', 'pago' in totalPayload);
            console.log('  - pagoTexto presente?', 'pagoTexto' in totalPayload);
            
            const response = await pedidoService.update(id, totalPayload);
            
            console.log('🔍 RESPOSTA do sync automático:');
            console.log('  - response completo:', response);
            console.log('  - response.data:', response.data);
            if (response.data) {
              console.log('  - response.data.pago:', response.data.pago);
              console.log('  - response.data.pagoTexto:', response.data.pagoTexto);
            }
            
            if (response.success) {
              console.log('✅ Valor total atualizado no backend com sucesso');
            } else {
              console.error('❌ Erro ao atualizar valor total:', response.error);
            }
          } catch (error) {
            console.error('❌ Erro ao atualizar valor total:', error);
          }
        }
      }
    };
    
    // Usar timeout para evitar múltiplas chamadas
    const timeoutId = setTimeout(updateTotalInBackend, 500);
    return () => clearTimeout(timeoutId);
  }, [itens, isNew, id, form.valor]);

  // Monitorar se há itens em modo de edição para ajustar altura do container
  useEffect(() => {
    const hasEditItem = itens.some(item => item.isNew === true) || itemsInEditMode.size > 0;
    setHasItemInEdit(hasEditItem);
  }, [itens, itemsInEditMode]);

  // Callback para quando um item entra/sai do modo de edição
  const handleEditModeChange = useCallback((itemIndex, isInEditMode) => {
    setItemsInEditMode(prev => {
      const newSet = new Set(prev);
      if (isInEditMode) {
        newSet.add(itemIndex);
      } else {
        newSet.delete(itemIndex);
      }
      return newSet;
    });
  }, []);

  // Callback para quando um item é salvo com sucesso
  const handleItemSaveSuccess = useCallback(() => {
    setShowGlobalItemSuccess(true);
    setTimeout(() => setShowGlobalItemSuccess(false), 2000);
  }, []);

  // Função manual para forçar atualização do total no backend
  const forceUpdateTotal = async () => {
    if (isNew) return; // Não atualizar se é um pedido novo
    
    try {
      // Aguardar um pouco para garantir que os itens foram atualizados
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const novoTotal = calculateTotal();
      
      console.log('=== FORCE UPDATE TOTAL ===');
      console.log('Pedido ID:', id);
      console.log('Total calculado:', novoTotal);
      console.log('Valor atual no form:', form.valor);
      console.log('========================');
      
      // CORREÇÃO: Usar apenas campos específicos para evitar nulls
      const totalPayload = {
        valor: novoTotal,
        // Preservar campos essenciais do form atual para evitar nulls
        status: form.status || 'Pendente',
        extensao: form.extensao,
        prazoEntrega: form.prazoEntrega,
        formaEntrega: form.formaEntrega,
        formaPagamento: form.formaPagamento,
        pessoa: form.quemPediu,
        user: form.atribuirAo,
        // REMOVIDO: pago e pagoTexto não devem ser modificados nas atualizações
        solicitadoExtensaoEm: form.solicitacaoExtensao || null,
        solicitadoIbeEm: form.solicitacaoIbe || null,
        detalhes: form.detalhes || null
      };
      
      console.log('Payload que será enviado (com campos preservados):', totalPayload);
      console.log('🔍 Verificação força total - Campos de pagamento:');
      console.log('  - pago presente?', 'pago' in totalPayload);
      console.log('  - pagoTexto presente?', 'pagoTexto' in totalPayload);
      console.log('URL será: /pedidos/' + id);
      console.log('Método: PUT');
      const response = await pedidoService.update(id, totalPayload);
      console.log('🔍 RESPOSTA do forceUpdateTotal:');
      console.log('  - response completo:', response);
      console.log('  - response.data:', response.data);
      if (response.data) {
        console.log('  - response.data.pago:', response.data.pago);
        console.log('  - response.data.pagoTexto:', response.data.pagoTexto);
      }
      
      if (response.success) {
        console.log('✅ Valor total forçado com sucesso!');
        // Atualizar o form local também
        setForm(prev => ({
          ...prev,
          valor: novoTotal
        }));
      } else {
        console.error('❌ Erro ao forçar atualização do total:', response.error);
      }
    } catch (error) {
      console.error('❌ Erro ao forçar atualização do total:', error);
    }
  };

  const validate = () => {
    // Para usuários restritos, validar apenas campos visíveis
    if (isRestrictedUser()) {
      if (!form.extensao) return 'A extensão é obrigatória.';
      if (!form.formaEntrega) return 'A forma de recebimento é obrigatória.';
      if (!form.formaPagamento) return 'A forma de pagamento é obrigatória.';
      return '';
    }
    
    // Para usuários normais (admin) - apenas campos realmente obrigatórios
    if (!form.extensao) return 'A extensão é obrigatória.';
    // Quem Pediu, Atribuir Ao, Prazo, Forma Pagamento são OPCIONAIS
    return '';
  };

  const handleSubmit = async (e) => {
    console.log('🎯 [handleSubmit] Formulário submetido!');
    e.preventDefault();
    // Prevenir submit padrão do formulário
    // Usar funções específicas de botão em vez do submit do form
    handleNormalSubmit();
  };

  const confirmSubmit = async (isDraftSave = false) => {
    console.log('✅ [confirmSubmit] Confirmando submit, isDraftSave:', isDraftSave);
    setShowConfirm(false);
    
    // Verificar se há itens que precisam de pessoa destinatária mas não têm
    const itemsSemPessoa = getItemsMissingDestinationPerson();
    
    if (itemsSemPessoa.length > 0) {
      console.log('⚠️ [confirmSubmit] Itens sem pessoa destinatária:', itemsSemPessoa.length);
      // Se há itens sem pessoa destinatária, mostrar modal de confirmação
      setPendingSubmitData({ isDraftSave });
      setShowMissingPersonConfirm(true);
      return;
    }
    
    console.log('✅ [confirmSubmit] Sem problemas, prosseguindo para proceedWithSubmit');
    // Se não há problemas com pessoas destinatárias, prosseguir normalmente
    await proceedWithSubmit(isDraftSave);
  };

  // Função que realmente executa o submit
  const proceedWithSubmit = async (isDraftSave = false) => {
    console.log('🚀 [proceedWithSubmit] INICIANDO ENVIO, isDraftSave:', isDraftSave);
    console.log('  - form.extensao:', form.extensao);
    console.log('  - form.formaEntrega:', form.formaEntrega);
    console.log('  - itens.length:', itens.length);
    
    setLoading(true);
    setError('');
    setFieldErrors({}); // Limpar erros de campo anteriores

    try {
      // Atualizar itens sem pessoa destinatária para null se necessário
      await updateItemsWithNullPerson();
      
      // Preparar dados do pedido (excluindo campos que não devem ser modificados)
      const { 
        codigo, 
        solicitacaoExtensao, 
        solicitacaoIbe, 
        quemPediu, 
        atribuirAo, 
        pago,           // REMOVIDO: não deve ser modificado nas atualizações
        pagoTexto,      // REMOVIDO: não deve ser modificado nas atualizações
        ...pedidoData 
      } = form;
      
      // Mapear itens para o formato correto do backend
      console.log('=== DEBUG FORMATAÇÃO DE ITENS ===');
      console.log('itens array original:', itens);
      console.log('itens.length:', itens.length);
      
      const itensFormatados = itens.map((item, index) => {
        console.log(`Formatando item ${index}:`, item);
        
        // DEBUG específico para campos de matrícula
        if (item.curso || item.tipoCurso || item.periodo) {
          console.log(`=== DEBUG MATRÍCULA ITEM ${index} ===`);
          console.log('item.curso:', item.curso, typeof item.curso);
          console.log('item.tipoCurso:', item.tipoCurso, typeof item.tipoCurso);
          console.log('item.periodo:', item.periodo, typeof item.periodo);
          console.log('============================');
        }
        
        const itemFormatado = {
          ...item,
          // Mapear pessoaDestinataria para pessoa (campo do backend)
          pessoa: item.pessoaDestinataria || null,
          // Garantir que campos obrigatórios estejam presentes
          detalhamento: item.detalhamento || item.observacao || null,
          // Campo de troca por livros
          trocaPorLivros: !!item.trocaPorLivros,
          // Campos específicos para matrícula - garantir tipos corretos
          curso: item.curso ? parseInt(item.curso) : null,
          tipoCurso: item.tipoCurso || null,
          periodo: item.periodo || null
        };
        
        // DEBUG específico para campos formatados de matrícula
        if (itemFormatado.curso || itemFormatado.tipoCurso || itemFormatado.periodo) {
          console.log(`=== DEBUG MATRÍCULA FORMATADO ${index} ===`);
          console.log('itemFormatado.curso:', itemFormatado.curso, typeof itemFormatado.curso);
          console.log('itemFormatado.tipoCurso:', itemFormatado.tipoCurso, typeof itemFormatado.tipoCurso);
          console.log('itemFormatado.periodo:', itemFormatado.periodo, typeof itemFormatado.periodo);
          console.log('=====================================');
        }
        
        console.log(`Item ${index} formatado:`, itemFormatado);
        return itemFormatado;
      });
      
      console.log('=== RESULTADO FORMATAÇÃO ===');
      console.log('itensFormatados:', itensFormatados);
      console.log('itensFormatados.length:', itensFormatados.length);
      console.log('================================');
      
      // Função para converter undefined em null e remover campos protegidos
      const sanitizeData = (obj) => {
        console.log('=== DEBUG SANITIZE DATA ===');
        console.log('Input para sanitizeData:', obj);
        
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
          console.log(`Processando key: ${key}, value:`, value, typeof value);
          
          // PROTEÇÃO: Nunca incluir campos de pagamento nos payloads
          if (key === 'pago' || key === 'pagoTexto') {
            console.log(`🛡️ Campo protegido removido do payload: ${key} = ${value}`);
            continue; // Pular esses campos completamente
          }
          
          // Converter undefined em null
          // IMPORTANTE: Manter string vazia para permitir validação backend
          sanitized[key] = value === undefined ? null : value;
          console.log(`Key ${key} sanitizada para:`, sanitized[key]);
        }
        
        console.log('Resultado sanitizado:', sanitized);
        console.log('sanitized.itens:', sanitized.itens);
        console.log('sanitized.extensao:', sanitized.extensao, '(tipo:', typeof sanitized.extensao, ')');
        console.log('sanitized.formaEntrega:', sanitized.formaEntrega, '(tipo:', typeof sanitized.formaEntrega, ')');
        console.log('=========================');
        return sanitized;
      };
      
      const payload = sanitizeData({
        ...pedidoData,
        // Manter os nomes dos campos como o backend espera
        solicitacaoExtensao: solicitacaoExtensao || null,
        solicitacaoIbe: solicitacaoIbe || null,
        // Mapear campos do frontend para o backend
        pessoa: quemPediu || null, // quemPediu -> pessoa
        user: atribuirAo || null,  // atribuirAo -> user
        // Nome não será gerado automaticamente - campo opcional
        nome: pedidoData.nome || null,
        valor: calculateTotal(),
        // Definir status baseado no modo de salvamento
        status: isDraftSave ? 'Rascunho' : 'Pendente', // Se não é rascunho, sempre Pendente
        itens: itensFormatados // Usar itens formatados
      });
      
      console.log('=== DEBUG PAYLOAD DETALHADO ===');
      console.log('Payload sendo enviado:', payload);
      console.log('=== VERIFICAÇÃO DE ITENS ===');
      console.log('itens array original:', itens);
      console.log('itens length:', itens.length);
      console.log('itensFormatados:', itensFormatados);
      console.log('itensFormatados length:', itensFormatados.length);
      console.log('payload.itens:', payload.itens);
      console.log('payload.itens length:', payload.itens ? payload.itens.length : 'N/A');
      console.log('payload.itens === itensFormatados:', payload.itens === itensFormatados);
      console.log('JSON.stringify(payload.itens):', JSON.stringify(payload.itens));
      console.log('==========================');
      console.log('🔍 Verificação de segurança - Campos de pagamento no payload:');
      console.log('  - pago presente?', 'pago' in payload);
      console.log('  - pagoTexto presente?', 'pagoTexto' in payload);
      if ('pago' in payload || 'pagoTexto' in payload) {
        console.error('❌ ERRO: Campos de pagamento encontrados no payload! Isso não deveria acontecer.');
      } else {
        console.log('✅ Payload limpo - sem campos de pagamento');
      }
      console.log('Status definido:', payload.status);
      console.log('Itens no payload:', payload.itens);
      console.log('Itens formatados:', itensFormatados);
      console.log('Quantidade de itens:', itens.length);
      console.log('=== DEBUG CONFIRMSUBMIT ===');
      console.log('ID do pedido:', id);
      console.log('isNew:', isNew);
      console.log('isDraftSave:', isDraftSave);
      console.log('actionType:', actionType);
      console.log('form.status:', form.status);
      console.log('Vai usar:', isNew ? 'CREATE' : 'UPDATE');
      console.log('==========================');
      
      // Log específico para itens novos
      const itensNovos = itensFormatados.filter(item => item.isNew === true);
      if (itensNovos.length > 0) {
        console.log('=== ITENS NOVOS DETECTADOS ===');
        itensNovos.forEach((item, index) => {
          console.log(`Item novo ${index}:`, {
            tipoProduto: item.tipoProduto,
            produto: item.produto,
            quantidade: item.quantidade,
            valorUnitario: item.valorUnitario,
            valorUnitarioTipo: typeof item.valorUnitario,
            detalhamento: item.detalhamento,
            observacao: item.observacao,
            pessoaDestinataria: item.pessoaDestinataria,
            pessoa: item.pessoa, // Campo mapeado para o backend
            isNew: item.isNew
          });
        });
        console.log('===============================');
      }

      console.log('🚀 [FRONTEND] ENVIANDO REQUISIÇÃO PARA O BACKEND:');
      console.log('  - Tipo:', isNew ? 'CREATE (POST)' : 'UPDATE (PUT)');
      console.log('  - URL:', isNew ? '/api/pedidos' : `/api/pedidos/${id}`);
      console.log('  - Campos obrigatórios:');
      console.log('    * extensao:', payload.extensao, '(tipo:', typeof payload.extensao, ')');
      console.log('    * formaEntrega:', payload.formaEntrega, '(tipo:', typeof payload.formaEntrega, ')');
      console.log('  - Campos opcionais:');
      console.log('    * formaPagamento:', payload.formaPagamento);
      console.log('    * prazoEntrega:', payload.prazoEntrega);
      console.log('    * quemPediu:', payload.quemPediu);
      console.log('    * atribuirAo:', payload.atribuirAo);
      console.log('  - Total de itens:', payload.itens?.length || 0);
      console.log('  - Payload completo:', JSON.stringify(payload, null, 2));

      const result = isNew 
        ? await pedidoService.create(payload)
        : await pedidoService.update(id, payload);

      console.log('✅ [FRONTEND] RESPOSTA RECEBIDA DO BACKEND:');
      console.log('  - result.success:', result.success);
      console.log('  - result.error:', result.error);
      console.log('  - result.message:', result.message);
      console.log('  - result.validationErrors:', result.validationErrors);
      console.log('  - result completo:', result);
      console.log('  - result.data:', result.data);
      if (result.data) {
        console.log('  - result.data.pago:', result.data.pago);
        console.log('  - result.data.pagoTexto:', result.data.pagoTexto);
      }

      if (result.success) {
        const successMsg = isDraftSave 
          ? 'Rascunho salvo com sucesso!'
          : 'Pedido salvo com sucesso! Redirecionando...';
        setSuccessMessage(successMsg);
        setSuccess(true);
        
        // Se não for rascunho, atualizar o status local para refletir a mudança
        if (!isDraftSave) {
          setForm(prev => ({
            ...prev,
            status: 'Pendente'
          }));
        }
        
        // Se for rascunho, não redirecionar - permanecer na tela de edição
        if (!isDraftSave) {
          setTimeout(() => navigate('/pedidos'), 2000);
        } else {
          // Para rascunhos, atualizar o ID se for um novo pedido
          if (isNew && result.data) {
            const pedidoId = result.data.idProduto || result.data.id;
            console.log('🔄 Novo rascunho criado com ID:', pedidoId);
            console.log('🔄 Dados retornados do backend:', result.data);
            console.log('🔄 ATENÇÃO: Vai redirecionar - isso pode causar recarregamento dos dados!');
            if (pedidoId) {
              // Redirecionar para a página de edição com o novo ID
              setTimeout(() => {
                console.log('🔄 REDIRECIONANDO para /pedidos/' + pedidoId + '/edit');
                navigate(`/pedidos/${pedidoId}/edit`, { replace: true });
              }, 500);
            }
          } else {
            // Limpar mensagem de sucesso após alguns segundos
            setTimeout(() => {
              setSuccess(false);
              setSuccessMessage('');
            }, 3000);
          }
        }
      } else {
        // Processar erros de validação do backend
        if (result.validationErrors && result.validationErrors.length > 0) {
          const errors = {};
          result.validationErrors.forEach(err => {
            // express-validator usa 'param' para o nome do campo
            const fieldName = err.param || err.path;
            errors[fieldName] = err.msg;
          });
          setFieldErrors(errors);
          setError('Por favor, corrija os campos destacados em vermelho.');
        } else {
          setError(result.error || 'Erro ao salvar pedido');
        }
      }
    } catch (err) {
      setError('Erro ao salvar pedido');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para validar campos obrigatórios condicionalmente
  const validateForm = (isDraftMode = false) => {
    const errors = [];
    
    // VALIDAÇÃO FRONTEND REMOVIDA
    // Deixar o backend validar os campos obrigatórios
    // Isso permite que os erros sejam retornados com os nomes dos campos
    // e destacados em vermelho individualmente
    
    // Apenas validar itens para usuários restritos (eles não têm acesso ao backend)
    if (isRestrictedUser() && itens.length === 0) {
      errors.push('Pelo menos um item deve ser adicionado');
    }
    
    return errors;
  };

  // Função para lidar com submissão normal
  const handleNormalSubmit = () => {
    console.log('📝 [handleNormalSubmit] Iniciando submit normal');
    console.log('=== ESTADO COMPLETO DO FORM ===');
    console.log('  - form.extensao:', form.extensao, '(tipo:', typeof form.extensao, ', vazio?:', form.extensao === '', ')');
    console.log('  - form.formaEntrega:', form.formaEntrega, '(tipo:', typeof form.formaEntrega, ', vazio?:', form.formaEntrega === '', ')');
    console.log('  - form.solicitacaoIbe:', form.solicitacaoIbe, '(tipo:', typeof form.solicitacaoIbe, ', vazio?:', form.solicitacaoIbe === '', ')');
    console.log('  - form completo:', JSON.stringify(form, null, 2));
    console.log('  - itens.length:', itens.length);
    console.log('=================================');
    
    const errors = validateForm(false);
    console.log('  - Erros de validação:', errors);
    
    if (errors.length > 0) {
      console.log('❌ [handleNormalSubmit] BLOQUEADO POR VALIDAÇÃO:', errors);
      setError(errors.join(', '));
      return;
    }
    
    console.log('✅ [handleNormalSubmit] Validação OK, mostrando modal de confirmação');
    setActionType('normal');
    setIsDraft(false);
    setShowConfirm(true);
  };

  // Função para lidar com submissão como rascunho
  const handleDraftSubmit = () => {
    console.log('💾 [handleDraftSubmit] Iniciando submit como rascunho');
    console.log('  - form.extensao:', form.extensao);
    console.log('  - form.formaEntrega:', form.formaEntrega);
    
    const errors = validateForm(true);
    console.log('  - Erros de validação (modo rascunho):', errors);
    
    if (errors.length > 0) {
      console.log('❌ [handleDraftSubmit] BLOQUEADO POR VALIDAÇÃO:', errors);
      setError(errors.join(', '));
      return;
    }
    
    console.log('✅ [handleDraftSubmit] Validação OK, salvando como rascunho');
    setActionType('draft');
    setIsDraft(true);
    confirmSubmit(true); // Salvar diretamente como rascunho sem confirmação
  };

  // Função para enviar pedido (transformar rascunho em pendente)
  const handleSendOrder = () => {
    const errors = validateForm(false);
    
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }
    
    setActionType('send');
    setIsDraft(false);
    setShowConfirm(true);
  };

  // Função para verificar se está em modo de criação ou editando rascunho (para exibir campos condicionais)
  const isInCreationMode = () => {
    return isNew || form.status === 'Rascunho';
  };

  // Função para determinar se um campo é obrigatório (considera modo rascunho em criação e edição)
  const isFieldRequired = (fieldName) => {
    // Para usuários restritos, apenas alguns campos são obrigatórios
    if (isRestrictedUser()) {
      const restrictedRequired = ['extensao', 'formaEntrega', 'formaPagamento'];
      return restrictedRequired.includes(fieldName);
    }
    
    // Para usuários normais (admin):
    // Apenas extensão e forma de recebimento são obrigatórios
    // Todos os outros campos (quemPediu, atribuirAo, prazoEntrega, formaPagamento) são OPCIONAIS
    const alwaysRequired = ['extensao', 'formaEntrega'];
    return alwaysRequired.includes(fieldName);
  };

  // Função para iniciar exclusão do pedido
  const handleDeletePedido = () => {
    setShowDeletePedidoConfirm(true);
  };

  // Função para confirmar exclusão do pedido
  const confirmDeletePedido = async () => {
    setShowDeletePedidoConfirm(false);
    setLoading(true);
    setError('');

    try {
      const result = await pedidoService.deletePedido(id);
      
      if (result.success) {
        setSuccessMessage('Pedido excluído com sucesso! Redirecionando...');
        setSuccess(true);
        setTimeout(() => navigate('/pedidos'), 1500);
      } else {
        setError(result.error || 'Erro ao excluir pedido');
      }
    } catch (err) {
      setError('Erro ao excluir pedido');
      console.error('Erro ao excluir pedido:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para cancelar exclusão do pedido
  const cancelDeletePedido = () => {
    setShowDeletePedidoConfirm(false);
  };

  if (loadingData) {
    return (
      <div className="flex justify-center items-center py-8">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-green-600" />
        <span className="ml-2 text-gray-600">Carregando...</span>
      </div>
    );
  }

  // Verificar permissões
  const requiredPermission = isNew ? 'pedidos.create' : 'pedidos.update';
  if (!hasPermission(requiredPermission)) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          Você não tem permissão para {isNew ? 'criar' : 'editar'} pedidos.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 p-2 sm:p-4">
      <div className="w-full mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
              {isNew ? 'Novo Pedido' : 'Editar Pedido'}
            </h1>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4 mt-2">
              <p className="text-gray-600 text-sm sm:text-base">
                {isNew ? 'Criar um novo pedido' : `Editando pedido #${form.codigo || id}`}
              </p>
              {!isNew && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      form.status === 'Concluido' 
                        ? 'bg-green-100 text-green-800' 
                        : form.status === 'Pendente'
                        ? 'bg-yellow-100 text-yellow-800'
                        : form.status === 'Rascunho'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {form.status || 'Pendente'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 bg-green-50 p-3 sm:px-4 sm:py-2 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faDollarSign} className="text-green-600" />
                      <span className="text-sm font-medium text-green-700">Valor Total:</span>
                    </div>
                    <span className="text-lg sm:text-xl font-bold text-green-600 bg-white px-3 py-1 rounded-md shadow-sm">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-row justify-center items-center space-x-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3 mt-4">
            <button
              type="button"
              onClick={() => navigate('/pedidos')}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center text-sm sm:text-base"
              title="Voltar para listagem de pedidos"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="sm:mr-2" />
              <span className="hidden sm:inline">Voltar</span>
            </button>
            
            {!isNew && form.status !== 'Rascunho' && (
              <button
                type="button"
                onClick={handleDeletePedido}
                disabled={loading}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center text-sm sm:text-base"
                title="Excluir Pedido"
              >
                <FontAwesomeIcon icon={faTrash} className="sm:mr-2" />
                <span className="hidden sm:inline">Excluir Pedido</span>
              </button>
            )}
            {/* Botão Salvar Rascunho - disponível para todos os usuários em criação ou quando status é Rascunho */}
            {(() => {
              const shouldShow = (isNew || form.status === 'Rascunho');
              console.log('🔘 [BOTÃO RASCUNHO] Condições:', {
                isNew,
                'form.status': form.status,
                shouldShow
              });
              return shouldShow;
            })() && (
              <button
                type="button"
                onClick={(e) => {
                  console.log('🖱️ [CLICK] Botão Salvar Rascunho foi CLICADO!');
                  handleDraftSubmit();
                }}
                disabled={loading}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50 transition-colors flex items-center justify-center text-sm sm:text-base"
                title="Salvar como Rascunho"
              >
                <FontAwesomeIcon icon={faFileAlt} className="sm:mr-2" />
                <span className="hidden sm:inline">Salvar Rascunho</span>
              </button>
            )}
            {/* Botão Enviar Pedido - quando status é Rascunho */}
            {form.status === 'Rascunho' && (
              <button
                type="button"
                onClick={handleSendOrder}
                disabled={loading}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center font-medium text-sm sm:text-base"
                title={loading ? 'Enviando...' : 'Enviar Pedido'}
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faSave} spin={loading} className="sm:mr-2" />
                <span className="hidden sm:inline">{loading ? 'Enviando...' : 'Enviar Pedido'}</span>
              </button>
            )}
            {/* Botão padrão para criação/edição normal */}
            {(() => {
              const shouldShow = form.status !== 'Rascunho';
              console.log('🔘 [BOTÃO NORMAL] Condições:', {
                'form.status': form.status,
                isNew,
                loading,
                shouldShow
              });
              return shouldShow;
            })() && (
              <button
                type="button"
                onClick={(e) => {
                  console.log('🖱️ [CLICK] Botão Criar/Salvar Pedido foi CLICADO!');
                  console.log('  - isNew:', isNew);
                  console.log('  - form.status:', form.status);
                  console.log('  - loading:', loading);
                  handleNormalSubmit();
                }}
                disabled={loading}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center font-medium text-sm sm:text-base"
                title={loading ? 'Salvando...' : (isNew ? 'Criar Pedido' : 'Salvar Pedido')}
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faSave} spin={loading} className="sm:mr-2" />
                <span className="hidden sm:inline">{loading ? 'Salvando...' : (isNew ? 'Criar Pedido' : 'Salvar Pedido')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Informação sobre modo rascunho */}
        {(isNew || form.status === 'Rascunho') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faFileAlt} className="text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 mb-1">
                  {isNew ? 'Modo de Criação' : 'Pedido em Rascunho'}
                </h3>
                <p className="text-sm text-blue-700">
                  {isNew ? (
                    <>Você pode salvar como <strong>rascunho</strong> (campos opcionais) ou <strong>criar o pedido</strong> (todos os campos obrigatórios preenchidos).</>
                  ) : (
                    <>Este pedido está em rascunho. Você pode continuar editando com campos opcionais ou <strong>enviar o pedido</strong> (validação completa).</>
                  )}
                  {' '}Rascunhos podem ser editados posteriormente para adicionar informações pendentes.
                </p>

              </div>
            </div>
          </div>
        )}

        {/* Mensagens */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-3 sm:px-4 py-3 rounded-md text-sm sm:text-base">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-3 sm:px-4 py-3 rounded-md text-sm sm:text-base">
            {successMessage}
          </div>
        )}

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white rounded-t-lg">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 px-3 sm:px-6 pt-3 sm:pt-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab('dados')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap ${
              activeTab === 'dados'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={faFileAlt} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Dados do Pedido</span>
            <span className="sm:hidden">Dados</span>
            {calculateTotal() > 0 && (
              <span className="ml-1 sm:ml-2 bg-green-100 text-green-800 text-xs font-medium px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                {formatCurrency(calculateTotal())}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('itens')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'itens'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={faShoppingCart} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Itens ({calculateTotalQuantity()} unidades)</span>
            <span className="sm:hidden">Itens ({calculateTotalQuantity()})</span>
            {/* Indicador de itens sem pessoa destinatária */}
            {getItemsMissingDestinationPerson().length > 0 && (
              <span className="ml-1 sm:ml-2 bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">
                {getItemsMissingDestinationPerson().length} sem pessoa
              </span>
            )}
          </button>
          {/* Aba Comprovantes - NÃO disponível para usuários restritos */}
          {!isRestrictedUser() && hasPermission('pedidos.upload_comprovante') && (
            <button
              onClick={() => setActiveTab('comprovantes')}
              className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap ${
                activeTab === 'comprovantes'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              disabled={isNew}
              title={isNew ? "Salve o pedido primeiro para enviar comprovantes" : ""}
            >
              <FontAwesomeIcon icon={faFileAlt} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Comprovantes</span>
              <span className="sm:hidden">Comprv.</span>
              {isNew && (
                <span className="ml-1 text-xs text-gray-400">(salve primeiro)</span>
              )}
            </button>
          )}
        </nav>
      </div>

      {/* `noValidate`: o pedido tem validação própria em `validateForm`, com
          mensagens em português que ficam na tela. O balão nativo do navegador
          interrompia o submit antes disso e mostrava um campo por vez, no
          idioma do navegador. */}
      <form id="pedido-form" onSubmit={handleSubmit} noValidate className="pedido-form space-y-4 sm:space-y-6">
        {/* Tab: Dados do Pedido */}
        {activeTab === 'dados' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h2 className="text-base sm:text-lg font-medium text-gray-900">
                <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-green-600" />
                <span className="hidden sm:inline">Informações do Pedido</span>
                <span className="sm:hidden">Informações</span>
              </h2>
            </div>

            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              {/* Linha 1: Código, Forma de Pagamento, Extensão (para usuários restritos) */}
              {/* Linha 1: Código, Fica pronto em, Extensão (para usuários não restritos) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código
                  </label>
                  <input
                    type="text"
                    name="codigo"
                    value={form.codigo || (isNew ? 'Automático' : '')}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                    placeholder="Automático"
                  />
                </div>

                {!isRestrictedUser() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fica pronto em{isFieldRequired('prazoEntrega') ? ' *' : ''}
                    </label>
                    <select
                      name="prazoEntrega"
                      value={form.prazoEntrega || ''}
                      onChange={handleChange}
                      required={isFieldRequired('prazoEntrega')}
                      className={getFieldClass('prazoEntrega', fieldErrors)}
                    >
                      <option value="">Selecione</option>
                      {selectOptions.prazosEntrega?.map(prazo => (
                        <option key={prazo.idPrazoEntrega} value={prazo.idPrazoEntrega}>{prazo.nome}</option>
                      ))}
                    </select>
                    {fieldErrors.prazoEntrega && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.prazoEntrega}</p>
                    )}
                  </div>
                )}

                {isRestrictedUser() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FontAwesomeIcon icon={faCreditCard} className="mr-1 text-green-600" />
                      Forma de pagamento{isFieldRequired('formaPagamento') ? ' *' : ''}
                    </label>
                    <select
                      name="formaPagamento"
                      value={form.formaPagamento || ''}
                      onChange={handleChange}
                      required={isFieldRequired('formaPagamento')}
                      className={getFieldClass('formaPagamento', fieldErrors)}
                    >
                      <option value="">Selecione</option>
                      {selectOptions.formasPagamento?.map(forma => (
                        <option key={forma.idFormaPagamento} value={forma.idFormaPagamento}>{forma.nome}</option>
                      ))}
                    </select>
                    {fieldErrors.formaPagamento && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.formaPagamento}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1 text-green-600" />
                    Extensão{isFieldRequired('extensao') ? ' *' : ''}
                  </label>
                  <select
                    name="extensao"
                    value={form.extensao || ''}
                    onChange={handleChange}
                    required={isFieldRequired('extensao')}
                    className={getFieldClass('extensao', fieldErrors)}
                  >
                    <option value="">Selecione</option>
                    {extensoes.map(extensao => (
                      <option key={extensao.idExtensao} value={extensao.idExtensao}>{extensao.nome}</option>
                    ))}
                  </select>
                  {fieldErrors.extensao && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.extensao}</p>
                  )}
                  {/* Indicador de itens com pessoas destinatárias */}
                  {hasItemsWithDestinationPerson() && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                      {itens.filter(item => item.pessoaDestinataria && item.pessoaDestinataria !== '').length} 
                      {itens.filter(item => item.pessoaDestinataria && item.pessoaDestinataria !== '').length === 1 
                        ? ' item possui pessoa destinatária selecionada' 
                        : ' itens possuem pessoas destinatárias selecionadas'}
                    </p>
                  )}
                </div>
              </div>

              {/* Linha 2: Datas - ocultar para usuários restritos */}
              {!isRestrictedUser() && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-green-600" />
                      <span className="hidden sm:inline">Solicitação na extensão{isFieldRequired('solicitacaoExtensao') ? ' *' : ''}</span>
                      <span className="sm:hidden">Sol. extensão{isFieldRequired('solicitacaoExtensao') ? ' *' : ''}</span>
                    </label>
                    <input
                      type="date"
                      name="solicitacaoExtensao"
                      value={form.solicitacaoExtensao || ''}
                      onChange={handleChange}
                      required={isFieldRequired('solicitacaoExtensao')}
                      className={getFieldClass('solicitacaoExtensao', fieldErrors)}
                    />
                    {fieldErrors.solicitacaoExtensao && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.solicitacaoExtensao}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-green-600" />
                      <span className="hidden sm:inline">Solicitação no IBE *</span>
                      <span className="sm:hidden">Sol. IBE *</span>
                    </label>
                    <input
                      type="date"
                      name="solicitacaoIbe"
                      value={form.solicitacaoIbe || ''}
                      onChange={handleChange}
                      required
                      className={getFieldClass('solicitacaoIbe', fieldErrors)}
                    />
                    {fieldErrors.solicitacaoIbe && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.solicitacaoIbe}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Linha 3: Forma de Entrega (sempre) e Forma de Pagamento (apenas usuários não restritos) */}
              {/* Para usuários restritos, mostrar apenas Forma de Entrega */}
              <div className={`grid grid-cols-1 ${!isRestrictedUser() ? 'sm:grid-cols-2' : ''} gap-3 sm:gap-4`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon icon={faTruck} className="mr-1 text-green-600" />
                    <span className="hidden sm:inline">Forma de Recebimento{isFieldRequired('formaEntrega') ? ' *' : ''}</span>
                    <span className="sm:hidden">Recebimento{isFieldRequired('formaEntrega') ? ' *' : ''}</span>
                  </label>
                  <select
                    name="formaEntrega"
                    value={form.formaEntrega || ''}
                    onChange={handleChange}
                    required={isFieldRequired('formaEntrega')}
                    className={getFieldClass('formaEntrega', fieldErrors)}
                  >
                    <option value="">Selecione</option>
                    {selectOptions.formasEntrega?.map(forma => (
                      <option key={forma.idFormaEntrega} value={forma.idFormaEntrega}>{forma.nome}</option>
                    ))}
                  </select>
                  {fieldErrors.formaEntrega && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.formaEntrega}</p>
                  )}
                </div>

                {!isRestrictedUser() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FontAwesomeIcon icon={faCreditCard} className="mr-1 text-green-600" />
                      <span className="hidden sm:inline">Forma de pagamento{isFieldRequired('formaPagamento') ? ' *' : ''}</span>
                      <span className="sm:hidden">Pagamento{isFieldRequired('formaPagamento') ? ' *' : ''}</span>
                    </label>
                    <select
                      name="formaPagamento"
                      value={form.formaPagamento || ''}
                      onChange={handleChange}
                      required={isFieldRequired('formaPagamento')}
                      className={getFieldClass('formaPagamento', fieldErrors)}
                    >
                      <option value="">Selecione</option>
                      {selectOptions.formasPagamento?.map(forma => (
                        <option key={forma.idFormaPagamento} value={forma.idFormaPagamento}>{forma.nome}</option>
                      ))}
                    </select>
                    {fieldErrors.formaPagamento && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.formaPagamento}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Linha 4: Quem Pediu e Atribuir A - ocultar para usuários restritos */}
              {!isRestrictedUser() && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FontAwesomeIcon icon={faUser} className="mr-1 text-green-600" />
                      Quem pediu{isFieldRequired('quemPediu') ? ' *' : ''}
                    </label>
                    <select
                      name="quemPediu"
                      value={form.quemPediu || ''}
                      onChange={handleChange}
                      required={isFieldRequired('quemPediu')}
                      disabled={loadingPessoas || !form.extensao}
                      className={getFieldClass('quemPediu', fieldErrors, "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed text-sm")}
                    >
                      <option value="">
                        {loadingPessoas ? 'Carregando...' : 'Selecione'}
                      </option>
                      {pessoasExtensao.map(pessoa => (
                        <option key={pessoa.idPessoa} value={pessoa.idPessoa}>{pessoa.nome}</option>
                      ))}
                    </select>
                    {fieldErrors.quemPediu && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.quemPediu}</p>
                    )}
                    {!form.extensao && (
                      <p className="text-xs text-gray-500 mt-1">Selecione uma extensão primeiro</p>
                    )}
                    {form.extensao && pessoasExtensao.length === 0 && !loadingPessoas && (
                      <p className="text-xs text-yellow-600 mt-1">Nenhuma pessoa ativa encontrada nesta extensão</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Atribuir ao(a){isFieldRequired('atribuirAo') ? ' *' : ''}
                    </label>
                    <select
                      name="atribuirAo"
                      value={form.atribuirAo || ''}
                      onChange={handleChange}
                      required={isFieldRequired('atribuirAo')}
                      disabled={loadingFuncionarios}
                      className={getFieldClass('atribuirAo', fieldErrors, "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed text-sm")}
                    >
                      <option value="">
                        {loadingFuncionarios ? 'Carregando funcionários...' : 'Selecione um funcionário'}
                      </option>
                      {funcionarios.map(funcionario => (
                        <option key={funcionario.idPessoa} value={funcionario.nome}>{funcionario.nome}</option>
                      ))}
                    </select>
                    {fieldErrors.atribuirAo && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.atribuirAo}</p>
                    )}
                    {funcionarios.length === 0 && !loadingFuncionarios && (
                      <p className="text-xs text-yellow-600 mt-1">Nenhum funcionário encontrado</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Itens */}
        {activeTab === 'itens' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50 flex flex-row justify-between items-center gap-2 sm:gap-4">
              <div className="flex flex-row items-center gap-2 sm:gap-4">
                <h2 className="text-base sm:text-lg font-medium text-gray-900">
                  <FontAwesomeIcon icon={faShoppingCart} className="mr-2 text-green-600" />
                  <span className="hidden sm:inline">Itens do Pedido</span>
                  <span className="sm:hidden">Itens</span>
                </h2>
                <span className="bg-blue-100 text-blue-800 text-xs sm:text-sm font-medium px-2 py-1 rounded-full">
                  {calculateTotalQuantity()} {calculateTotalQuantity() === 1 ? 'unidade' : 'unidades'}
                </span>
              </div>
              <button
                type="button"
                onClick={addItem}
                data-add-item
                className="flex-shrink-0 px-3 sm:px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                title="Adicionar Item"
              >
                <FontAwesomeIcon icon={faPlus} className="sm:mr-2" />
                <span className="hidden sm:inline">Adicionar Item</span>
              </button>
            </div>

            {/* Alerta sobre itens sem pessoa destinatária */}
            {getItemsMissingDestinationPerson().length > 0 && (
              <div className="px-3 sm:px-6 py-3 bg-amber-50 border-b border-amber-200">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-amber-800 font-medium">
                      Atenção: {getItemsMissingDestinationPerson().length} {getItemsMissingDestinationPerson().length === 1 ? 'item requer pessoa destinatária' : 'itens requerem pessoas destinatárias'}
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      {getItemsMissingDestinationPerson().length === 1 
                        ? 'Este item será salvo sem pessoa destinatária se você prosseguir.'
                        : 'Estes itens serão salvos sem pessoas destinatárias se você prosseguir.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3 sm:p-6">
              {itens.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <FontAwesomeIcon icon={faShoppingCart} className="text-3xl sm:text-4xl text-gray-300 mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base">Nenhum item adicionado ao pedido.</p>
                  <button
                    type="button"
                    onClick={addItem}
                    className="mt-3 sm:mt-4 px-3 sm:px-4 py-2 text-green-600 border border-green-300 rounded-md hover:bg-green-50 transition-colors text-sm sm:text-base"
                  >
                    <FontAwesomeIcon icon={faPlus} className="sm:mr-2" />
                    <span className="hidden sm:inline">Adicionar Primeiro Item</span>
                    <span className="sm:hidden">Adicionar Item</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4 relative">
                  {/* Container com rolagem para os itens */}
                  <div className="relative">
                    {/* Indicador de quantidade de itens - mais visível no mobile */}
                    {itens.length > 3 && !hasItemInEdit && (
                      <div className="scroll-indicator text-xs sm:text-sm">
                        <span className="hidden sm:inline">Scroll para ver todos ({itens.length} itens)</span>
                        <span className="sm:hidden">{itens.length} itens</span>
                      </div>
                    )}
                    
                    {/* Indicador quando em modo de edição */}
                    {hasItemInEdit && (
                      <div className="bg-blue-50 border border-blue-200 text-blue-700 text-xs p-2 rounded mb-2 flex items-center">
                        <FontAwesomeIcon icon={faEdit} className="mr-2" />
                        <span>Formulário expandido para edição - Use scroll se necessário</span>
                      </div>
                    )}
                    
                    <div 
                      className={`items-container ${hasItemInEdit ? 'editing-mode max-h-screen sm:max-h-none' : 'max-h-80 sm:max-h-64'} overflow-y-auto pr-1 sm:pr-2 space-y-2 custom-scrollbar border border-gray-100 rounded-lg p-2 sm:p-3`}
                      onScroll={handleScroll}
                      style={{
                        scrollBehavior: 'smooth',
                        ...(hasItemInEdit && {
                          maxHeight: 'calc(100vh - 200px)' // Garante espaço para header e botões em mobile
                        })
                      }}
                    >
                      {itens.map((item, index) => (
                        <div key={index} className="relative">
                          {/* Indicador de posição para itens em edição */}
                          {item.isNew && (
                            <div className="absolute -left-2 top-0 bottom-0 w-1 bg-green-500 rounded-full animate-pulse z-10" />
                          )}
                          <ItemPedidoCard
                            key={index}
                            item={item}
                            index={index}
                            itens={itens}
                            onUpdate={updateItem}
                            onRemove={removeItem}
                            produtos={selectOptions.produtos || []}
                            tipos={selectOptions.tipos || []}
                            pessoasExtensao={pessoasExtensao}
                            onEditModeChange={handleEditModeChange}
                            onSaveSuccess={handleItemSaveSuccess}
                            formaPagamentoId={form.formaPagamento}
                            formasPagamento={selectOptions.formasPagamento || []}
                            extensaoId={form.extensao}
                          />
                        </div>
                      ))}
                    </div>
                    
                    {/* Gradiente superior para indicar mais conteúdo acima */}
                    {scrollInfo.canScrollUp && (
                      <div className="absolute top-0 left-0 right-0 h-4 sm:h-6 bg-gradient-to-b from-white via-white/80 to-transparent pointer-events-none z-15 rounded-t-lg" />
                    )}
                    
                    {/* Gradiente inferior para indicar mais conteúdo abaixo */}
                    {scrollInfo.canScrollDown && (
                      <div className="absolute bottom-0 left-0 right-0 h-4 sm:h-6 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-15 rounded-b-lg" />
                    )}
                  </div>

                  {/* Botão flutuante para adicionar item - adaptado para mobile */}
                  <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50">
                    <button
                      type="button"
                      onClick={addItem}
                      className="floating-add-btn w-12 h-12 sm:w-14 sm:h-14 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-all duration-200 flex items-center justify-center hover:scale-110"
                      title="Adicionar novo item - Será adicionado ao final da lista"
                    >
                      <FontAwesomeIcon icon={faPlus} className="text-lg sm:text-xl" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Comprovantes */}
        {activeTab === 'comprovantes' && !isNew && hasPermission('pedidos.upload_comprovante') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-base sm:text-lg font-medium text-gray-900">
                <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-green-600" />
                <span className="hidden sm:inline">Comprovantes de Pagamento</span>
                <span className="sm:hidden">Comprovantes</span>
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Envie até 4 comprovantes de pagamento para este pedido
              </p>
            </div>

            <div className="p-3 sm:p-6">
              <ComprovanteUpload
                pedidoId={id}
                pedidoStatus={form.status}
                pedidoPago={form.pagoTexto === 'Sim'}
                onUploadComplete={(comprovantes) => {
                  console.log('Comprovantes enviados:', comprovantes);
                  // Pode adicionar feedback adicional aqui se necessário
                }}
              />
            </div>
          </div>
        )}

        {/* Mensagem para pedidos novos ou sem permissão */}
        {activeTab === 'comprovantes' && (isNew || !hasPermission('pedidos.upload_comprovante')) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 text-center">
              <FontAwesomeIcon icon={faFileAlt} className="text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {isNew ? 'Comprovantes Indisponíveis' : 'Sem Permissão'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNew 
                  ? 'Para enviar comprovantes de pagamento, você precisa primeiro salvar o pedido.'
                  : 'Você não tem permissão para enviar comprovantes de pagamento.'
                }</p>
              <button
                type="button"
                onClick={() => setActiveTab('dados')}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Ir para Dados do Pedido
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Mensagem de sucesso global para itens */}
      {showGlobalItemSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none">
          {/* Backdrop semitransparente */}
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
          {/* Mensagem de sucesso */}
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-2xl flex items-center animate-bounce relative">
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            <span className="font-medium">Item salvo com sucesso!</span>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          // Determinar se é rascunho baseado no actionType
          const isRascunhoSave = actionType === 'draft';
          confirmSubmit(isRascunhoSave);
        }}
        title={actionType === 'draft'
          ? 'Salvar Rascunho' 
          : actionType === 'send'
            ? 'Enviar Pedido' 
            : (isNew ? 'Criar Pedido' : 'Salvar Alterações')
        }
        message={actionType === 'draft'
          ? 'Tem certeza que deseja salvar este pedido como rascunho?' 
          : actionType === 'send'
            ? 'Tem certeza que deseja enviar este pedido? Ele será validado e enviado para aprovação.'
            : `Tem certeza que deseja ${isNew ? 'criar este pedido' : 'salvar as alterações'}?`}
        loading={loading}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={showDeletePedidoConfirm}
        onClose={cancelDeletePedido}
        onConfirm={confirmDeletePedido}
        title="Excluir Pedido"
        message="Tem certeza que deseja excluir este pedido? Esta ação não pode ser desfeita."
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        type="danger"
        loading={loading}
      />

      {/* Modal de Confirmação de Mudança de Extensão */}
      <ConfirmModal
        isOpen={showExtensionChangeConfirm}
        onClose={cancelExtensionChange}
        onConfirm={confirmExtensionChange}
        title="Mudança de Extensão"
        message="Existem itens com pessoas destinatárias selecionadas. Ao alterar a extensão, todas as pessoas destinatárias serão removidas e você precisará selecioná-las novamente. Deseja continuar?"
        confirmText="Sim, Continuar"
        cancelText="Cancelar"
        type="warning"
        loading={false}
      />

      {/* Modal de Confirmação para Salvamento com Pessoas Destinatárias Faltando */}
      <ConfirmModal
        isOpen={showMissingPersonConfirm}
        onClose={cancelSubmitWithMissingPersons}
        onConfirm={confirmSubmitWithMissingPersons}
        title="Pessoas Destinatárias Faltando"
        message={`Existem ${getItemsMissingDestinationPerson().length} ${getItemsMissingDestinationPerson().length === 1 ? 'item que requer pessoa destinatária mas não tem nenhuma selecionada' : 'itens que requerem pessoas destinatárias mas não têm nenhuma selecionada'}. Estes itens serão salvos sem pessoa destinatária. Deseja continuar?`}
        confirmText="Sim, Salvar Assim Mesmo"
        cancelText="Cancelar"
        type="warning"
        loading={loading}
      />
      </div>
    </div>
  );
};

export default PedidoEdit;
